/** Bodygraph SVG visualization module. */
const Bodygraph = {
  CENTER_ID_MAP: {
    Head: "head-center",
    Ajna: "ajna-center",
    Throat: "throat-center",
    G: "g-center",
    Heart: "heart-center",
    Spleen: "splenic-center",
    "Solar Plexus": "solar-plexus-center",
    Sacral: "sacral-center",
    Root: "root-center",
  },

  /** Which gates sit in which center — the engine's own table, mirrored from
   *  backend/engine/constants.py CENTER_GATES (bodygraph-consciousness.test.js
   *  checks they agree). The trimmed `_paint_block` payload carries no
   *  `center_a`/`center_b`, so centers are always read off the gate. */
  CENTER_GATES: {
    Head: [61, 63, 64],
    Ajna: [4, 11, 17, 24, 43, 47],
    Throat: [8, 12, 16, 20, 23, 31, 33, 35, 45, 56, 62],
    G: [1, 2, 7, 10, 13, 15, 25, 46],
    Heart: [21, 26, 40, 51],
    "Solar Plexus": [6, 22, 30, 36, 37, 49, 55],
    Spleen: [18, 28, 32, 44, 48, 50, 57],
    Sacral: [3, 5, 9, 14, 27, 29, 34, 42, 59],
    Root: [19, 38, 39, 41, 52, 53, 54, 58, 60],
  },

  /** Gate number → center name, all 64, filled from CENTER_GATES below the
   *  module the way the engine derives its own reverse lookup. */
  GATE_TO_CENTER: {},

  /** The 36 channels as gate pairs, in the engine's own order — mirrored from
   *  backend/engine/constants.py CHANNELS (bodygraph-channels.test.js checks
   *  they agree). Pairs only: the center comes from GATE_TO_CENTER. */
  CHANNELS: [
    [1, 8],
    [2, 14],
    [3, 60],
    [4, 63],
    [5, 15],
    [6, 59],
    [7, 31],
    [9, 52],
    [10, 20],
    [10, 34],
    [10, 57],
    [11, 56],
    [12, 22],
    [13, 33],
    [16, 48],
    [17, 62],
    [18, 58],
    [19, 49],
    [20, 34],
    [20, 57],
    [21, 45],
    [23, 43],
    [24, 61],
    [25, 51],
    [26, 44],
    [27, 50],
    [28, 38],
    [29, 46],
    [30, 41],
    [32, 54],
    [34, 57],
    [35, 36],
    [37, 40],
    [39, 55],
    [42, 53],
    [47, 64],
  ],

  /** Gate number → the gates it can complete a channel with, filled from
   *  CHANNELS below the module. One partner for most gates; three each for the
   *  four Integration gates, which is why this answers a LIST and not a gate. */
  GATE_PARTNERS: {},

  /** The SVG's own box, 412×707. A caller that sizes the drawing by the height
   *  it has left needs the ratio in JS: a percentage height resolves too late
   *  to give the flex basis a width (css/chart.css, `.chart-stage-chart`). */
  SVG_ASPECT: 412 / 707,

  /** The all-open chart a stage draws when it has no chart to show yet, or
   *  none it may assert: every center undefined, no channels, no active gates
   *  — an honest silhouette, drawn by the real painter. */
  silhouetteData() {
    return {
      centers: Object.keys(this.CENTER_ID_MAP).map((name) => ({ name: name, defined: false })),
      gates: [],
      defined_channels: [],
      hanging_gates: [],
    };
  },

  /** What a card's stage shows for a chart payload: "tiers" (measured across a
   *  birth-time window), "unfilled" (an uncertain time without tiers: the
   *  verdict beats any geometry in the payload, or the fence leaks), "plain"
   *  (a drawable chart), or null. `verdict` is the card's birth_time_verdict,
   *  read when the payload carries no birth_time of its own. The chat cards
   *  and the MCP viewers all decide it here. */
  chartOutcome(data, verdict) {
    data = data || {};
    if (data.item_tiers) return "tiers";
    const confidence = (data.birth_time && data.birth_time.confidence) || verdict || "";
    if (confidence === "roughly" || confidence === "unknown") return "unfilled";
    return Array.isArray(data.centers) && data.centers.length ? "plain" : null;
  },

  /** The mandala wheel: the 64 gates in wheel order, and where one sits on it.
   *  Here, not on `Chart`, because the transits page draws the ring without
   *  chart.js; keep one copy of the table. */
  MANDALA: {
    GATE_SEQUENCE: [
      41, 19, 13, 49, 30, 55, 37, 63, 22, 36, 25, 17, 21, 51, 42, 3, 27, 24, 2, 23, 8, 20, 16, 35, 45, 12, 15, 52, 39,
      53, 62, 56, 31, 33, 7, 4, 29, 59, 40, 64, 47, 6, 46, 18, 48, 57, 32, 50, 28, 44, 1, 43, 14, 34, 9, 5, 26, 11, 10,
      58, 38, 54, 61, 60,
    ],

    /** Gate 41 opens the wheel, at 302° — the mandala's zero. */
    GATE_41_START: 302,
    GATE_ANGLE: 360 / 64,

    /** The ring's proportions, as fractions of the stage's outer radius. */
    RING_MID_RATIO: 0.85,
    RING_BAND_RATIO: 0.12,

    /** The middle of a gate's sector, in mandala degrees; NaN for a gate that
     *  is not one of the 64. */
    gateAngleDeg(gateNum) {
      const idx = this.GATE_SEQUENCE.indexOf(Number(gateNum));
      if (idx === -1) return NaN;
      return this.GATE_41_START + idx * this.GATE_ANGLE + this.GATE_ANGLE / 2;
    },

    /** The middle of a gate's LINE, used wherever an activation carries no
     *  longitude (older stored results, transit payloads): within half a line
     *  of the true longitude. */
    gateLineAngleDeg(gateNum, line) {
      const idx = this.GATE_SEQUENCE.indexOf(Number(gateNum));
      if (idx === -1) return NaN;
      if (!(line >= 1 && line <= 6)) return this.gateAngleDeg(gateNum);
      return this.GATE_41_START + idx * this.GATE_ANGLE + (line - 0.5) * (this.GATE_ANGLE / 6);
    },

    /** Mandala degrees → canvas radians (the canvas's y axis points down). */
    toCanvasRad(deg) {
      return (-deg * Math.PI) / 180;
    },
  },

  _svgTemplatePromise: null,

  _isDark() {
    return document.documentElement.getAttribute("data-theme") === "dark";
  },

  /** Every colour the painter uses, as the design token it reads and the value */
  COLOR_TOKENS: {
    // Centers
    centerDefined: { token: "--hd-center-defined", light: "#F5DCC5", dark: "#3D2415" },
    centerUndefined: { token: "--hd-center-undefined", light: "#FAF8F5", dark: "#2A2724" },
    centerStrokeDefined: { token: "--hd-center-stroke-defined", light: "#A8663D", dark: "#9A6842" },
    centerStrokeUndefined: { token: "--hd-center-stroke-undefined", light: "#D4CDC4", dark: "#4A453E" },
    // Channels. --hd-design is the one token the CSS already shared with the
    // painter (the planet table's red); the channel and the planet label are
    // the same red on purpose.
    channelDesign: { token: "--hd-design", light: "#C75050", dark: "#E05555" },
    channelPersonality: { token: "--hd-personality", light: "#2A2420", dark: "#EDE9E3" },
    channelBorder: { token: "--hd-channel-border", light: "rgba(26,22,19,0.08)", dark: "rgba(200,195,185,0.12)" },
    // Gate pills
    gateActive: { token: "--hd-gate-active", light: "#B0603A", dark: "#C47040" },
    gateInactive: { token: "--hd-gate-inactive", light: "#EDE8E1", dark: "#35322D" },
    gateTextActive: { token: "--hd-gate-text-active", light: "#FFFFFF", dark: "#FFFFFF" },
    gateTextInactive: { token: "--hd-gate-text-inactive", light: "#7A7470", dark: "#908880" },
    // The numeral on a pill wearing the Personality ink. That ink is near-black
    // on paper and BONE in the dark, so this is the one gate colour that has to
    // flip with the theme rather than stay white in both.
    gateTextOnPersonality: { token: "--hd-gate-text-on-personality", light: "#FFFFFF", dark: "#1A1613" },
    // A gate both people of a connection hold: a white numeral over the two
    // stripes, haloed so it reads on either colour.
    gateTextBoth: { token: "--hd-gate-text-both", light: "#FFFFFF", dark: "#FFFFFF" },
    gateTextBothHalo: { token: "--hd-gate-text-both-halo", light: "rgba(0,0,0,0.45)", dark: "rgba(0,0,0,0.45)" },
    // Gate hover — the gold a hovered gate washes its row with, shared with the CSS.
    gateHighlight: { token: "--gate-highlight", light: "#FFD700", dark: "#FFD700" },
    gateHighlightText: { token: "--hd-gate-highlight-text", light: "#1A1613", dark: "#1A1613" },
    // The circuit view (paint's `circuits` option, the fullscreen stage only):
    // every channel in its family's colour, the four the channel list's group
    // headings and pills already wear (chart.css `.ci-circuit[data-circuit]`).
    circuitIntegration: { token: "--circuit-integration", light: "#5b8a82", dark: "#82b3a9" },
    circuitIndividual: { token: "--circuit-individual", light: "#b8862a", dark: "#d4a557" },
    circuitCollective: { token: "--circuit-collective", light: "#5a78a3", dark: "#87a3cc" },
    circuitTribal: { token: "--circuit-tribal", light: "#a84d52", dark: "#c97a7e" },
    // …and the ink for what has no family. A hanging gate is HALF a channel,
    // in no circuit, so the circuit view draws it in one neutral graphite.
    circuitHanging: { token: "--circuit-hanging", light: "#73706c", dark: "#8d8a86" },
    // The bridges view (paint's `bridges` option): each area of definition in
    // its own tint, top area first; the first pair is the ordinary defined warm.
    areaInk1: { token: "--hd-area-1", light: "#B0603A", dark: "#D98A5C" },
    areaFill1: { token: "--hd-area-1-fill", light: "#F5DCC5", dark: "#3D2415" },
    areaInk2: { token: "--hd-area-2", light: "#6B5A8E", dark: "#A996CC" },
    areaFill2: { token: "--hd-area-2-fill", light: "#E6DEF0", dark: "#2E2638" },
    areaInk3: { token: "--hd-area-3", light: "#4F6F7A", dark: "#86AAB6" },
    areaFill3: { token: "--hd-area-3-fill", light: "#DBE6EA", dark: "#1F2D33" },
    areaInk4: { token: "--hd-area-4", light: "#6B7D3A", dark: "#A8B872" },
    areaFill4: { token: "--hd-area-4-fill", light: "#E4EAD2", dark: "#2A2F1C" },
    // A bridging gate is nobody's: no area tint and no side, so it is drawn in
    // the text ink alone. A hanging gate that meets no bridge goes faint.
    bridgeInk: { token: "--hd-bridge-ink", light: "#1A1613", dark: "#EDE9E3" },
    areaFaint: { token: "--hd-area-faint", light: "#D9D3CB", dark: "#3A3530" },
    // Canvas ring (the fullscreen stage, chart-stage.js)
    ringBorder: { token: "--hd-ring-border", light: "rgba(26,22,19,0.15)", dark: "rgba(237,233,227,0.2)" },
    ringHoverBg: { token: "--hd-ring-hover-bg", light: "rgba(255,215,0,0.25)", dark: "rgba(255,215,0,0.25)" },
    ringText: { token: "--hd-ring-text", light: "#8A837C", dark: "#716B64" },
    ringTextActive: { token: "--hd-ring-text-active", light: "#1A1613", dark: "#EDE9E3" },
    ringDesignBg: { token: "--hd-ring-design-bg", light: "rgba(199,80,80,0.1)", dark: "rgba(224,85,85,0.15)" },
    ringPersonalityBg: {
      token: "--hd-ring-personality-bg",
      light: "rgba(26,22,19,0.06)",
      dark: "rgba(237,233,227,0.1)",
    },
    ringBothBg: { token: "--hd-ring-both-bg", light: "rgba(196,98,45,0.12)", dark: "rgba(224,122,62,0.2)" },
    labelBg: { token: "--hd-label-bg", light: "rgba(255,255,255,0.9)", dark: "rgba(20,18,16,0.85)" },
    // Planet labels
    planetDesign: { token: "--hd-design", light: "#C75050", dark: "#E05555" },
    planetPersonality: { token: "--hd-planet-personality", light: "#2A2420", dark: "#D4CEC6" },
    labelDesignBorder: {
      token: "--hd-label-design-border",
      light: "rgba(199,80,80,0.3)",
      dark: "rgba(224,85,85,0.4)",
    },
    // The one grey an uncertain chart's "likely" shapes wear (see paintTiers):
    // deliberately neither ink, because a birth-time window never says which
    // side of the chart put a shape there.
    likely: { token: "--hd-likely", light: "#857E77", dark: "#8A837C" },
    likelyFill: { token: "--hd-likely-fill", light: "#E7E2DB", dark: "#33302C" },
    // The sky (paintSky): the one ink in this table that is not the person.
    // Cool, so a transit never reads as natal. `skyHatch` is for a channel the
    // sky holds end to end, which must not look like definition.
    sky: { token: "--hd-sky", light: "#3E6BB5", dark: "#8DB0F0" },
    skyHatch: { token: "--hd-sky-hatch", light: "rgba(62,107,181,0.55)", dark: "rgba(141,176,240,0.55)" },
  },

  /** A reader of design tokens: `read(name, fallback)` answers the computed
   *  value of a custom property, or the fallback. Call `done()` when finished.
   *  `forceLight` reads the light theme through a probe carrying
   *  `[data-theme="light"]`, which is how the PDF prints paper colours. */
  _tokenReader({ forceLight = false } = {}) {
    // No stylesheet to read — a page without a document, or a sandbox without
    // computed styles — answers the fallbacks, which are the table's own values.
    if (typeof getComputedStyle !== "function" || !document.documentElement) {
      return { read: (_name, fallback) => fallback, done: () => {} };
    }
    let host = document.documentElement;
    let probe = null;
    if (forceLight && document.body) {
      probe = document.createElement("div");
      probe.setAttribute("data-theme", "light");
      probe.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:0;height:0;";
      document.body.appendChild(probe);
      host = probe;
    }
    const styles = getComputedStyle(host);
    return {
      read: (name, fallback) => (styles.getPropertyValue(name) || "").trim() || fallback,
      done: () => {
        if (probe) probe.remove();
      },
    };
  },

  /** Theme palette: every COLOR_TOKENS entry resolved from the stylesheet, with
   *  the table's own value where a token is not declared. `forceLight` pins the
   *  light theme: the PDF export must never pick up dark-mode colours. */
  colors({ forceLight = false } = {}) {
    const dark = !forceLight && this._isDark();
    const tokens = this._tokenReader({ forceLight });
    const out = {};
    for (const [name, entry] of Object.entries(this.COLOR_TOKENS)) {
      out[name] = tokens.read(entry.token, dark ? entry.dark : entry.light);
    }
    tokens.done();
    return out;
  },

  /**
   * An empty host the template's size, attached off screen so getBBox and
   * getScreenCTM work, for an export sheet to draw from. The caller removes it.
   */
  offscreenHost() {
    const el = document.createElement("div");
    el.style.cssText = "position:fixed;left:-9999px;top:-9999px;width:412px;height:707px;";
    document.body.appendChild(el);
    return el;
  },

  /**
   * Load bodygraph SVG into a container element.
   * @param {HTMLElement} container
   * @returns {Promise<boolean>}
   */
  async load(container) {
    try {
      const text = await this._getSvgTemplate();
      container.innerHTML = text;
      const svg = container.querySelector("svg");
      // One image to a screen reader, not 64 gate numbers. The words are the
      // app's; the docs and the MCP viewers have no `t` and keep the bare SVG.
      if (svg && typeof window.t === "function") {
        svg.setAttribute("role", "img");
        svg.setAttribute("aria-label", window.t("people.tab_bodygraph"));
      }
      if (svg) {
        // Move ChannelBorders below channel groups so fills render on top
        const chart = svg.getElementById("BodyGraphChart");
        const borders = svg.getElementById("ChannelBorders");
        if (chart && borders) chart.insertBefore(borders, chart.firstChild);
      }
      return true;
    } catch (e) {
      console.warn("Failed to load bodygraph SVG:", e);
      return false;
    }
  },

  async _getSvgTemplate() {
    if (!this._svgTemplatePromise) {
      const svgUrl = window.CONFIG?.versionedAsset("/assets/bodygraph.svg") || "/assets/bodygraph.svg";
      this._svgTemplatePromise = fetch(svgUrl)
        .then((resp) => {
          if (!resp.ok) throw new Error(`Failed to load SVG: ${resp.status}`);
          return resp.text();
        })
        .catch((err) => {
          this._svgTemplatePromise = null;
          throw err;
        });
    }
    return this._svgTemplatePromise;
  },

  /**
   * Find a closed border path for pixel-perfect channel fill.
   * @returns {Element|null} — a closed <path> element, or null
   */
  _findBorderPath(svg, gateA, gateB) {
    const tryId = (id) => {
      const el = svg.getElementById(id);
      if (!el || el.tagName !== "path") return null;
      return (el.getAttribute("d") || "").includes("Z") ? el : null;
    };
    return (
      tryId(`border-${gateA}-${gateB}`) ||
      tryId(`border-${gateB}-${gateA}`) ||
      // SVG has border path mis-labeled as "border-25-52" for channel 25-51
      ((gateA === 25 && gateB === 51) || (gateA === 51 && gateB === 25) ? tryId("border-25-52") : null)
    );
  },

  /** (Re)create a 45° two-color stripe pattern in the SVG's defs: a 6px tile,
   *  half each colour. */
  _ensureStripePattern(svg, patId, fillA, fillB) {
    const period = 6;
    const split = 3;
    let defs = svg.querySelector("defs");
    if (!defs) {
      defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      svg.insertBefore(defs, svg.firstChild);
    }
    let pat = defs.querySelector(`[id="${patId}"]`);
    if (pat) pat.remove();
    defs.querySelector(`[id="${patId}-cross"]`)?.remove();
    pat = document.createElementNS("http://www.w3.org/2000/svg", "pattern");
    pat.id = patId;
    pat.setAttribute("patternUnits", "userSpaceOnUse");
    pat.setAttribute("width", String(period));
    pat.setAttribute("height", String(period));
    pat.setAttribute("patternTransform", "rotate(45)");
    const r1 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    r1.setAttribute("width", String(split));
    r1.setAttribute("height", String(period));
    r1.setAttribute("fill", fillA);
    pat.appendChild(r1);
    const r2 = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    r2.setAttribute("x", String(split));
    r2.setAttribute("width", String(period - split));
    r2.setAttribute("height", String(period));
    r2.setAttribute("fill", fillB);
    pat.appendChild(r2);
    defs.appendChild(pat);
  },

  /** Choose a hatch that crosses the channel instead of running along it.
   * One copy per SVG, with the base's owner marker and colors. Keep its tile
   * self-contained so PDF export does not need pattern inheritance. */
  _channelFill(svg, gate, fill) {
    const leg = svg.getElementById(`gate-${gate}`);
    if (leg?.getAttribute("data-stripe-angle") !== "-45" || !fill.startsWith("url(#")) return fill;
    const id = fill.slice(5, -1);
    if (id.endsWith("-cross")) return fill;
    const defs = svg.querySelector("defs");
    const base = defs?.querySelector(`[id="${id}"]`);
    if (!base) return fill;
    const crossId = `${id}-cross`;
    if (!defs.querySelector(`[id="${crossId}"]`)) {
      const pattern = base.cloneNode(true);
      pattern.id = crossId;
      pattern.setAttribute("patternTransform", "rotate(-45)");
      defs.appendChild(pattern);
    }
    return `url(#${crossId})`;
  },

  /** A gate leg is one silhouette. Older embedded SVGs used two ribbons. */
  _gatePaths(svg, gate) {
    const leg = svg.getElementById(`gate-${gate}`);
    if (leg) return [leg];
    return [svg.getElementById(`design-${gate}`), svg.getElementById(`personality-${gate}`)].filter(Boolean);
  },

  /** Fixed SVG coordinates avoid forcing layout during a paint or scrub. */
  _geometryMidpoint(el) {
    const value = el?.getAttribute("data-midpoint");
    if (!value) return null;
    const parts = value.split(",").map(Number);
    return parts.length === 2 && parts.every(Number.isFinite) ? { x: parts[0], y: parts[1] } : null;
  },

  /** Reset channel borders + groups and hide the integration anonymous group. */
  _resetChannels(svg, c) {
    const borders = svg.getElementById("ChannelBorders");
    if (borders) {
      borders.setAttribute("stroke", c.channelBorder);
      // Reset per-element fills/strokes so border paths start clean
      borders.querySelectorAll("path, rect").forEach((p) => {
        // The stamp first: it puts a highlighted stroke back before the
        // stroke is stripped, so a highlight never outlives the paint.
        this._unstampChannel(p);
        p.removeAttribute("fill");
        p.removeAttribute("stroke");
        // The sky's background mark thins a border path to 1 so its blue
        // outline reads as an outline. The width is not the sky's to keep:
        // left behind it overrides the group's own 2 on the next plain paint.
        p.removeAttribute("stroke-width");
        p.removeAttribute("data-cmp-ch");
        p.removeAttribute("data-cmp-holders");
      });
    }

    const chart = svg.getElementById("BodyGraphChart");
    if (chart) {
      for (const child of chart.children) {
        if (child.tagName === "g" && !child.id && child.getAttribute("fill-rule") === "nonzero") {
          child.style.display = "none";
          break;
        }
      }
      for (const child of chart.children) {
        if (child.tagName !== "g" || !child.id) continue;
        if (child.id === "ChannelBorders" || child.id === "Centers") continue;
        this._unstampChannel(child);
        child.setAttribute("fill", "none");
        // On a channel with no border path the sky marks the GROUP, and a
        // stroke there is inherited by every leg. No group carries one in the
        // asset, so clearing it is a reset, not a loss.
        child.removeAttribute("stroke");
        child.removeAttribute("stroke-width");
        child.removeAttribute("clip-path");
        child.removeAttribute("data-cmp-ch");
        child.removeAttribute("data-cmp-holders");
        child.querySelectorAll("path, rect").forEach((p) => {
          this._unstampChannel(p);
          p.removeAttribute("fill");
          p.removeAttribute("stroke");
          p.removeAttribute("stroke-width");
          // A bridge leg (_paintBridgeStub) is the one channel paint that dashes.
          p.removeAttribute("stroke-dasharray");
          p.removeAttribute("clip-path");
          p.removeAttribute("data-cmp-ch");
          p.removeAttribute("data-cmp-holders");
          p.style.removeProperty("display");
        });
      }
    }
  },

  // ── The channel stamp and the channel highlight ─────────────────
  //
  // paint() and paintTiers() stamp every element they paint for a channel with
  // `data-ch="<a>-<b>"` (lower gate first). The attribute is a space-separated
  // LIST, since one leg can carry several channels: read it with channelKeys()
  // or match `[data-ch~="1-8"]`, never `=== key`.

  /** The stamp's key for a channel: the lower gate first, whatever order the
   *  payload names them in. */
  channelKey(gateA, gateB) {
    const a = Number(gateA);
    const b = Number(gateB);
    return a < b ? `${a}-${b}` : `${b}-${a}`;
  },

  /** Stamp one painted element — or, for a channel group, its legs, which are
   *  what carry a stroke — with the channel's key, keeping any key already on it.
   *  Gates 10, 20, 34 and 57 share one leg each between six channels, so a stamp
   *  that overwrote would lose all but the last channel painted. */
  _stampChannel(el, key) {
    const targets = el.tagName === "g" ? el.querySelectorAll("path, rect") : [el];
    targets.forEach((target) => {
      const carried = this.channelKeys(target);
      if (carried.indexOf(key) !== -1) return;
      carried.push(key);
      target.setAttribute("data-ch", carried.join(" "));
    });
  },

  /** Every channel one painted element carries, in the order they painted it. */
  channelKeys(el) {
    const carried = el.getAttribute("data-ch");
    return carried ? carried.split(" ").filter(Boolean) : [];
  },

  /** The channels in painting order for the circuit view: Integration last, so
   *  the legs it shares with 10-34 and 20-57 end up in its own colour. Stable,
   *  so every other family keeps the payload's order. */
  _integrationLast(channels) {
    const integration = (ch) => (String(ch.circuit_group || "").toLowerCase() === "integration" ? 1 : 0);
    return channels.slice().sort((a, b) => integration(a) - integration(b));
  },

  /** Whether one painted element carries this channel. */
  carriesChannel(el, key) {
    const carried = el.getAttribute("data-ch");
    if (carried === null) return false;
    return carried === key || this.channelKeys(el).indexOf(key) !== -1;
  },

  /** Take the stamp and the highlight's memo off one element, restoring the
   *  stroke the highlight replaced, so a repaint starts from the shape. */
  _unstampChannel(el) {
    this._restoreChannelStroke(el);
    el.removeAttribute("data-ch");
  },

  /** Put back the stroke a highlight memoed, if one is memoed. */
  _restoreChannelStroke(el) {
    const stroke = el.getAttribute("data-ch-stroke");
    if (stroke === null) return;
    const width = el.getAttribute("data-ch-width") || "";
    if (stroke === "") el.removeAttribute("stroke");
    else el.setAttribute("stroke", stroke);
    if (width === "") el.removeAttribute("stroke-width");
    else el.setAttribute("stroke-width", width);
    el.removeAttribute("data-ch-stroke");
    el.removeAttribute("data-ch-width");
  },

  /** The stroke width a highlighted channel wears, in the SVG's own units. */
  CHANNEL_HIGHLIGHT_WIDTH: "3",

  /**
   * Light one channel in the drawing — the elements stamped with `key` get a
   * stroke in the hover gold and a thicker width — and restore every other
   * stamped element to the stroke it was painted with. `null` clears.
   *
   * The previous stroke and width are memoed on the element (`data-ch-stroke`,
   * `data-ch-width`) the way a pill memos its fill for highlightGates: a
   * channel's stroke is its fill, its tier's grey or a pattern's "none", and
   * none of that can be re-derived from a highlighted element. A repaint
   * resets everything through _resetChannels.
   *
   * @param {HTMLElement} container
   * @param {string|null} key — the channel, lower gate first ("1-8"), or null
   */
  highlightChannel(container, key) {
    const stamped = container.querySelectorAll("[data-ch]");
    if (!stamped.length) return;
    const c = this.colors();
    stamped.forEach((el) => {
      if (key !== null && this.carriesChannel(el, key)) {
        if (el.getAttribute("data-ch-stroke") === null) {
          el.setAttribute("data-ch-stroke", el.getAttribute("stroke") || "");
          el.setAttribute("data-ch-width", el.getAttribute("stroke-width") || "");
        }
        el.setAttribute("stroke", c.gateHighlight);
        el.setAttribute("stroke-width", this.CHANNEL_HIGHLIGHT_WIDTH);
        return;
      }
      this._restoreChannelStroke(el);
    });
  },

  /**
   * Show or hide the Variable arrows. Both directions, because correcting a
   * birth time either way repaints the same SVG in place.
   * @param {SVGElement} svg
   * @param {boolean} hidden
   */
  _setVariableArrowsHidden(svg, hidden) {
    const arrows = svg.getElementById("VariableArrows");
    if (arrows) arrows.style.display = hidden ? "none" : "";
  },

  /**
   * Strip everything paintTiers() stamps, so a repaint of the same SVG —
   * ordinary or uncertain — starts from a clean shape rather than inheriting a
   * previous chart's uncertainty.
   * @param {SVGElement} svg
   */
  _clearTierState(svg) {
    // _applyTierStyle() writes presentation ATTRIBUTES, so these have to be
    // removed as attributes; clearing el.style does nothing.
    for (const el of svg.querySelectorAll("[data-tier]")) {
      el.removeAttribute("data-tier");
      el.removeAttribute("stroke");
      el.removeAttribute("stroke-width");
      el.removeAttribute("stroke-dasharray");
    }
    for (const el of svg.querySelectorAll("[data-tier-fill]")) {
      el.removeAttribute("data-tier-fill");
      el.removeAttribute("data-tier-stroke");
      el.removeAttribute("data-tier-dash");
    }
  },

  // ── Which side defines a center ───────────────────────────────

  /** The width of the outline that says which side defined a center. Above the
   *  classic 2; in SVG user units, so a small chat card scales it down. The ink
   *  is raw, not tinted, to match the gate pills' two inks. */
  CENTER_SIDE_WIDTH: "3",

  /** The ring a gate activated on BOTH sides wears, over the Personality ink.
   *  A pill is a 16-unit circle, so this is as much ring as fits before it eats
   *  the numeral. */
  GATE_PILL_RING_WIDTH: "1.5",

  /** A pill's radius, as tools/build/bodygraph_pills.mjs lays it out. */
  GATE_PILL_RADIUS: 8,

  /** Ring a pill, or take its ring off (`color` null). */
  _ringPill(rect, color, dash = null) {
    const width = color ? parseFloat(this.GATE_PILL_RING_WIDTH) : 0;
    const r = this.GATE_PILL_RADIUS - width / 2;
    const cx = parseFloat(rect.getAttribute("x")) + parseFloat(rect.getAttribute("width")) / 2;
    const cy = parseFloat(rect.getAttribute("y")) + parseFloat(rect.getAttribute("height")) / 2;
    if (Number.isFinite(cx) && Number.isFinite(cy)) {
      rect.setAttribute("x", String(cx - r));
      rect.setAttribute("y", String(cy - r));
      rect.setAttribute("width", String(2 * r));
      rect.setAttribute("height", String(2 * r));
      rect.setAttribute("rx", String(r));
    }
    if (!color) {
      rect.removeAttribute("stroke");
      rect.removeAttribute("stroke-width");
      rect.removeAttribute("stroke-dasharray");
      return;
    }
    rect.setAttribute("stroke", color);
    rect.setAttribute("stroke-width", this.GATE_PILL_RING_WIDTH);
    if (dash) rect.setAttribute("stroke-dasharray", dash);
    else rect.removeAttribute("stroke-dasharray");
  },

  /** Which side defines each center: `"personality"` or `"design"`, keyed by the
   *  engine's center name. A center nothing defines is absent.
   *
   *  A center is conscious when its definition needs nothing unconscious: one
   *  channel conscious end to end is enough ("black has right of way", the
   *  64keys rule; see docs/internal/decisions.md). A `"both"` gate counts as
   *  conscious; hanging gates are not read. Centers come from GATE_TO_CENTER,
   *  never `center_a`/`center_b`, which the trimmed payload lacks. */
  centerConsciousness(data) {
    const seen = {};
    for (const ch of (data && data.defined_channels) || []) {
      // "unconscious" and not "both": a doubly-activated gate is available
      // consciously, so it does not make the channel depend on the Design side.
      const needsDesign = ch.gate_a_consciousness === "unconscious" || ch.gate_b_consciousness === "unconscious";
      for (const gate of [ch.gate_a, ch.gate_b]) {
        const center = this.GATE_TO_CENTER[gate];
        if (!center) continue;
        // Personality wins and never un-wins: one channel that is conscious end
        // to end defines the center on its own, whatever its others need.
        if (seen[center] !== "personality") seen[center] = needsDesign ? "design" : "personality";
      }
    }
    return seen;
  },

  /** Outline one defined center in the side that defines it. Only the stroke
   *  changes; the fill keeps the defined warm. */
  _paintCenterSide(el, side, c) {
    if (!side) return;
    el.setAttribute("stroke", side === "personality" ? c.channelPersonality : c.channelDesign);
    el.setAttribute("stroke-width", this.CENTER_SIDE_WIDTH);
  },

  /** The colour an ACTIVE gate pill wears, and the numeral that sits on it. */
  gatePillStyle(consciousness, c) {
    if (consciousness === "unconscious") {
      return { fill: c.channelDesign, ring: null, text: c.gateTextActive };
    }
    return {
      fill: c.channelPersonality,
      ring: consciousness === "both" ? c.channelDesign : null,
      text: c.gateTextOnPersonality,
    };
  },

  /** Every activated gate's consciousness, keyed by gate number. */
  gateConsciousness(data) {
    const map = {};
    for (const ch of (data && data.defined_channels) || []) {
      map[ch.gate_a] = ch.gate_a_consciousness;
      map[ch.gate_b] = ch.gate_b_consciousness;
    }
    for (const hg of (data && data.hanging_gates) || []) map[hg.gate] = hg.consciousness;
    return map;
  },

  /** The activated gates that complete NO channel at all, as a Set. */
  hangingOnlyGates(data) {
    const completing = new Set();
    for (const ch of (data && data.defined_channels) || []) {
      completing.add(ch.gate_a);
      completing.add(ch.gate_b);
    }
    const out = new Set();
    for (const hg of (data && data.hanging_gates) || []) {
      if (!completing.has(hg.gate)) out.add(hg.gate);
    }
    return out;
  },

  /** Strip the ring and the resting-colour memo from every gate pill.
   *  Painters rerun over the same live SVG, so every painter clears first,
   *  beside `_resetChannels`, or a stale ring or memo leaks into the next chart. */
  _resetGatePills(svg) {
    // The sky is layered ON TOP of a natal drawing, so it is cleared with it:
    // every painter calls this, so the next paint starts with no sky marks.
    this._clearSkyState(svg);
    const centersGroup = svg.getElementById("Centers");
    if (!centersGroup) return;
    centersGroup.querySelectorAll("rect[id], text[id]").forEach((el) => {
      if (el.tagName.toLowerCase() === "rect" && /^\d+$/.test(el.id)) this._ringPill(el, null);
      el.removeAttribute("stroke");
      el.removeAttribute("stroke-width");
      el.removeAttribute("stroke-dasharray");
      // The halo a numeral wears over a two-colour stripe is a stroke plus this;
      // left behind, it would revive the halo on the next stroke.
      el.removeAttribute("paint-order");
      // A bridge's numeral is set bold (the bridges view); the asset's own
      // weight is "normal", so that is what it goes back to.
      if (el.getAttribute("data-gate-bold") !== null) {
        el.setAttribute("font-weight", "normal");
        el.removeAttribute("data-gate-bold");
      }
      el.removeAttribute("data-gate-fill");
      el.removeAttribute("data-gate-ring");
      el.removeAttribute("data-gate-dash");
    });
  },

  // ── Bridging gates ────────────────────────────────────────────
  //
  // A bridging gate is one the person does NOT hold whose channel would join
  // two of their areas of definition: drawn as a dashed leg and a dashed pill
  // ring in --hd-likely, since it is nobody's activation. Drawn only when the
  // caller opts in with `showBridges: true` (the knowledge-base figures).

  /** Dash pattern of a bridge leg and pill, in SVG user units. */
  BRIDGE_DASH: "4 3",
  BRIDGE_STROKE_WIDTH: "1.25",

  /** The stub a bridge draws: `{gate, partner}` for every channel it names. */
  bridgeStubs(data) {
    const out = [];
    for (const bridge of (data && data.bridging_gates) || []) {
      const gate = parseInt(bridge.gate, 10);
      for (const key of bridge.channels || []) {
        const ends = String(key)
          .split("-")
          .map((n) => parseInt(n, 10));
        if (ends.length !== 2 || ends.some(isNaN)) continue;
        const partner = ends[0] === gate ? ends[1] : ends[0];
        if (partner !== gate) out.push({ gate, partner });
      }
    }
    return out;
  },

  /** Paint one bridge's leg of a channel. Only the bridge's own leg: its
   *  partner's half is what the chart holds, and must never be overwritten.
   *  Removes the hanging-gate clip, which would hide the bridge leg. */
  _paintBridgeStub(svg, gate, partner, c, { fill = c.likelyFill, stroke = c.likely } = {}) {
    const group = svg.getElementById(`${gate}-${partner}`) || svg.getElementById(`${partner}-${gate}`);
    if (group) group.removeAttribute("clip-path");
    this._fillGatePaths(svg, gate, fill, (el) => {
      el.setAttribute("stroke", stroke);
      el.setAttribute("stroke-width", this.BRIDGE_STROKE_WIDTH);
      el.setAttribute("stroke-dasharray", this.BRIDGE_DASH);
    });
  },

  // ── Areas of definition, and the bridges between them ─────────

  /** A centre's place in bodygraph order, top to bottom — the order
   *  CENTER_ID_MAP is written in, and the engine's _BODYGRAPH_CENTER_ORDER. */
  _centerRank(name) {
    const i = Object.keys(this.CENTER_ID_MAP).indexOf(name);
    return i === -1 ? Object.keys(this.CENTER_ID_MAP).length : i;
  },

  /** Every gate the chart holds: the payload's `gates`, and every gate its
   *  channels and hanging gates name, for a payload that carries no list. */
  _heldGates(data) {
    const held = new Set((data && data.gates) || []);
    for (const ch of (data && data.defined_channels) || []) {
      held.add(Number(ch.gate_a));
      held.add(Number(ch.gate_b));
    }
    for (const hg of (data && data.hanging_gates) || []) held.add(Number(hg.gate));
    return held;
  },

  /** The chart's areas of definition as lists of centre names, each in
   *  bodygraph order and ordered by their topmost centre (the tint order).
   *  `[]` for no definition. */
  definitionAreas(data) {
    const byRank = (a, b) => this._centerRank(a) - this._centerRank(b);
    const given = data && data.definition_groups;
    let groups;
    if (Array.isArray(given) && given.length) {
      groups = given.filter((group) => Array.isArray(group) && group.length).map((group) => group.slice());
    } else {
      const defined = new Set();
      for (const center of (data && data.centers) || []) if (center.defined) defined.add(center.name);
      const next = {};
      const link = (a, b) => {
        (next[a] = next[a] || new Set()).add(b);
        (next[b] = next[b] || new Set()).add(a);
      };
      for (const ch of (data && data.defined_channels) || []) {
        const a = this.GATE_TO_CENTER[ch.gate_a];
        const b = this.GATE_TO_CENTER[ch.gate_b];
        if (!a || !b) continue;
        defined.add(a);
        defined.add(b);
        link(a, b);
      }
      groups = [];
      const seen = new Set();
      for (const start of [...defined].sort(byRank)) {
        if (seen.has(start)) continue;
        const group = [];
        const queue = [start];
        seen.add(start);
        while (queue.length) {
          const center = queue.shift();
          group.push(center);
          for (const other of next[center] || []) {
            if (seen.has(other)) continue;
            seen.add(other);
            queue.push(other);
          }
        }
        groups.push(group);
      }
    }
    groups.forEach((group) => group.sort(byRank));
    return groups.sort((a, b) => this._centerRank(a[0]) - this._centerRank(b[0]));
  },

  /** Centre name → the index of its area in definitionAreas(), for the
   *  defined centres only. */
  _areaIndex(areas) {
    const index = {};
    areas.forEach((group, i) => group.forEach((center) => (index[center] = i)));
    return index;
  },

  /** The areas one unheld gate would touch if it were activated: its own */
  _bridgeTouch(gate, areaOf, held) {
    const touched = new Set();
    const channels = [];
    const own = this.GATE_TO_CENTER[gate];
    if (own in areaOf) touched.add(areaOf[own]);
    for (const partner of (this.GATE_PARTNERS[gate] || []).slice().sort((a, b) => a - b)) {
      if (!held.has(partner)) continue;
      channels.push(this.channelKey(gate, partner));
      const area = areaOf[this.GATE_TO_CENTER[partner]];
      if (area !== undefined) touched.add(area);
    }
    return { touched: [...touched].sort((a, b) => a - b), channels };
  },

  /** The chart's bridging gates, in the payload's shape */
  bridgingGates(data) {
    if (data && Array.isArray(data.bridging_gates)) return data.bridging_gates;
    const areas = this.definitionAreas(data);
    if (areas.length < 2) return [];
    const areaOf = this._areaIndex(areas);
    const held = this._heldGates(data);
    const out = [];
    for (let gate = 1; gate <= 64; gate += 1) {
      if (held.has(gate)) continue;
      const { touched, channels } = this._bridgeTouch(gate, areaOf, held);
      if (touched.length >= 2) out.push({ gate, center: this.GATE_TO_CENTER[gate], channels });
    }
    return out;
  },

  /** Which areas (indices into definitionAreas) one bridging gate would join —
   *  what the bridges view's tooltip names. `[]` for a gate that joins none. */
  bridgeJoins(data, gate) {
    const areas = this.definitionAreas(data);
    if (areas.length < 2) return [];
    const n = Number(gate);
    const held = this._heldGates(data);
    if (held.has(n)) return [];
    const { touched } = this._bridgeTouch(n, this._areaIndex(areas), held);
    return touched.length >= 2 ? touched : [];
  },

  /** The likely chart of a birth-time window: the shapes that hold across most */
  likelyChart(itemTiers) {
    const tiers = itemTiers || {};
    const held = (item) => this.drawnTier(item) !== null;
    const channels = (tiers.channels || []).filter(held).map((ch) => ({
      gate_a: Number(ch.gate_a),
      gate_b: Number(ch.gate_b),
      name: ch.name,
      circuit_group: ch.circuit_group,
      circuit: ch.circuit,
    }));
    const complete = new Set(channels.map((ch) => this.channelKey(ch.gate_a, ch.gate_b)));
    const hanging = (tiers.hanging || [])
      .filter(held)
      .filter((stub) => !complete.has(this.channelKey(stub.gate, stub.partner_gate)))
      .map((stub) => ({ gate: Number(stub.gate), partner_gate: Number(stub.partner_gate) }));
    const gates = new Set((tiers.gates || []).filter(held).map((item) => Number(item.gate)));
    const defined = new Set();
    for (const ch of channels) {
      for (const gate of [ch.gate_a, ch.gate_b]) {
        gates.add(gate);
        defined.add(this.GATE_TO_CENTER[gate]);
      }
    }
    for (const stub of hanging) gates.add(stub.gate);
    const chart = {
      sideless: true,
      centers: Object.keys(this.CENTER_ID_MAP).map((name) => ({ name, defined: defined.has(name) })),
      defined_channels: channels,
      hanging_gates: hanging,
      gates: [...gates].sort((a, b) => a - b),
    };
    chart.definition_groups = this.definitionAreas(chart);
    chart.bridging_gates = this.bridgingGates(chart);
    return chart;
  },

  /** The ink and fill of area `i`, cycling past the fourth — a quadruple split
   *  is the most the engine names. */
  _areaColors(i, c) {
    const n = (Math.max(0, i) % 4) + 1;
    return { ink: c[`areaInk${n}`], fill: c[`areaFill${n}`] };
  },

  /**
   * Paint the bodygraph SVG from chart data.
   * @param {HTMLElement} container
   * @param {Object} data — chart data (centers, gates, defined_channels,
   *   optionally bridging_gates)
   * @param {string} namespace — suffix for generated <defs> ids, so two charts on
   *   one page never share a pattern
   * @param {Object} [options] — `forceLight: true` paints the light palette even
   *   in dark mode (PDF export); `showBridges: true` draws `bridging_gates`
   *   dashed (the knowledge-base figures; off everywhere else); `circuits: true`
   *   fills every channel, both halves, in its family's colour keyed on
   *   `circuit_group` (the fullscreen stage's circuit view; off everywhere
   *   else — the app's ordinary bodygraphs keep the two sides), and greys every
   *   hanging gate, which belongs to no circuit, in `circuitHanging` — stub
   *   always, pill where the gate completes no channel at all. Centers and
   *   bridges paint the same either way. `bridges: true` is the bridges view
   *   (the person page's view control; the knowledge-base figures next): each
   *   area of definition in its own tint — centres, channels and pills — with
   *   both sides collapsed into "defined", every bridging gate drawn in the
   *   text ink alone (a dashed leg, a dashed ring, a bold numeral), a hanging
   *   gate that meets a bridge in its area's ink and every other hanging gate
   *   faint. It supersedes `showBridges`. A window-measured payload
   *   (`item_tiers`) paints its likely chart in either view (likelyChart).
   */
  paint(
    container,
    data,
    namespace = "",
    { forceLight = false, showBridges = false, circuits = false, bridges = false } = {},
  ) {
    const svg = container.querySelector("svg");
    if (!svg) return;
    // A chart measured across a birth-time window arrives with `item_tiers`
    // instead of centers/gates/channels, so it must not be painted the ordinary
    // way (an empty body). Circuit and bridges views paint the likely chart.
    if (data && data.item_tiers) {
      if (!circuits && !bridges) return this.paintTiers(container, data.item_tiers, namespace, { forceLight });
      data = this.likelyChart(data.item_tiers);
    }
    // No side is known on a likely chart, so nothing below may draw one: no
    // side outline on a centre, no side ink on a pill, and no Variable arrows,
    // which are read off the Design Sun's tone to the minute.
    const sideless = Boolean(data && data.sideless);
    // Correcting a birth time from roughly to exact repaints THIS SVG in place,
    // and the painter below only sets fills, so stale tier strokes must go.
    this._clearTierState(svg);
    this._setVariableArrowsHidden(svg, sideless);
    this._resetGatePills(svg);
    const c = this.colors({ forceLight });
    const activeGates = new Set(data.gates || []);
    const patId = `both-stripe${namespace}`;
    // The bridges view's areas: centre name → area index, and the bridges.
    const areaOf = bridges ? this._areaIndex(this.definitionAreas(data)) : {};
    const areaInk = (center) => this._areaColors(areaOf[center], c).ink;

    // ── Striped pattern for "both" consciousness ──
    this._ensureStripePattern(svg, patId, c.channelDesign, c.channelPersonality);

    // ── Centers ──
    const bySide = this.centerConsciousness(data);
    for (const center of data.centers || []) {
      const el = svg.getElementById(this.CENTER_ID_MAP[center.name]);
      if (!el) continue;
      const tint = bridges && center.defined && center.name in areaOf ? this._areaColors(areaOf[center.name], c) : null;
      el.setAttribute("fill", tint ? tint.fill : center.defined ? c.centerDefined : c.centerUndefined);
      el.setAttribute("stroke", tint ? tint.ink : center.defined ? c.centerStrokeDefined : c.centerStrokeUndefined);
      el.setAttribute("stroke-width", center.defined ? "2" : "1.5");
      el.removeAttribute("stroke-dasharray");
      if (center.defined && !tint && !sideless) this._paintCenterSide(el, bySide[center.name], c);
    }

    this._resetChannels(svg, c);

    // ── Colour defined channels ──
    //
    // Order matters on the four shared legs (10/20/34/57): the last channel to
    // paint a leg keeps it. In the circuit view Integration paints last.
    for (const ch of circuits ? this._integrationLast(data.defined_channels || []) : data.defined_channels || []) {
      const key = this.channelKey(ch.gate_a, ch.gate_b);
      const mark = (el) => this._stampChannel(el, key);
      if (bridges) {
        // Both ends of a channel are in one area by construction: the channel
        // is what put them there.
        const fill = areaInk(this.GATE_TO_CENTER[ch.gate_a]);
        this._paintChannel(svg, ch, fill, fill, mark);
      } else if (circuits) {
        const fill = this.circuitFill(ch.circuit_group, c);
        this._paintChannel(svg, ch, fill, fill, mark);
      } else if (sideless) {
        this._paintChannel(svg, ch, c.gateActive, c.gateActive, mark);
      } else {
        this._colorChannel(svg, ch, c, patId, mark);
      }
    }

    // ── Colour hanging gates (half-channels) ──
    //
    // The circuit view greys them (--circuit-hanging): no family applies. A stub
    // whose leg is shared (10/20/34/57) leaves it to the channel that owns it.
    // The bridges view keeps a bridge-facing stub in its area's ink.
    const hangingOnly = circuits || bridges ? this.hangingOnlyGates(data) : new Set();
    const sharesItsLeg = (gate, partner) =>
      !svg.getElementById(`${gate}-${partner}`) && !svg.getElementById(`${partner}-${gate}`);
    const bridgeList = bridges ? this.bridgingGates(data) : [];
    const bridgeSet = new Set(bridgeList.map((bridge) => Number(bridge.gate)));
    const bridgeHalf = (gate) =>
      (data.hanging_gates || []).some((hg) => hg.gate === gate && bridgeSet.has(Number(hg.partner_gate)));
    const hangingInk = (gate) => {
      const center = this.GATE_TO_CENTER[gate];
      return bridgeHalf(gate) && center in areaOf ? areaInk(center) : c.areaFaint;
    };
    for (const hg of data.hanging_gates || []) {
      if (!circuits && !bridges) {
        if (sideless) this._paintHangingGate(svg, hg.gate, hg.partner_gate, c.gateActive);
        else this._colorHangingGate(svg, hg, c, patId);
        continue;
      }
      if (hangingOnly.has(hg.gate) || !sharesItsLeg(hg.gate, hg.partner_gate)) {
        const center = this.GATE_TO_CENTER[hg.gate];
        const fill = !bridges
          ? c.circuitHanging
          : bridgeSet.has(Number(hg.partner_gate)) && center in areaOf
            ? areaInk(center)
            : c.areaFaint;
        this._paintHangingGate(svg, hg.gate, hg.partner_gate, fill);
      }
    }

    // ── Bridging gates: the missing leg, dashed ──
    const bridgeStubs = bridges
      ? this.bridgeStubs({ bridging_gates: bridgeList })
      : showBridges
        ? this.bridgeStubs(data)
        : [];
    const bridgeLook = bridges ? { fill: c.centerUndefined, stroke: c.bridgeInk } : {};
    for (const stub of bridgeStubs) this._paintBridgeStub(svg, stub.gate, stub.partner, c, bridgeLook);
    const bridgeGates = new Set(bridgeStubs.map((s) => s.gate));
    const bridgeRing = bridges ? c.bridgeInk : c.likely;
    const bridgeFill = bridges ? c.centerUndefined : c.gateInactive;

    // ── Gate pills ──
    //
    // An active pill wears the side that activated it; an inactive one stays
    // grey. In the circuit view a gate that completes nothing greys with its
    // stub. A likely chart has no side, so its pills take one "held" colour.
    const bySideGate = this.gateConsciousness(data);
    const pillStyle = (n) => {
      if (bridges) {
        const ink = hangingOnly.has(n) ? hangingInk(n) : areaInk(this.GATE_TO_CENTER[n]);
        return ink === c.areaFaint
          ? { fill: ink, ring: null, text: c.gateTextInactive }
          : { fill: ink || c.gateActive, ring: null, text: c.gateTextOnPersonality };
      }
      if (hangingOnly.has(n)) return { fill: c.circuitHanging, ring: null, text: c.gateTextActive };
      if (sideless) return { fill: c.gateActive, ring: null, text: c.gateTextActive };
      return this.gatePillStyle(bySideGate[n], c);
    };
    const centersGroup = svg.getElementById("Centers");
    if (centersGroup) {
      centersGroup.querySelectorAll("rect[id]").forEach((rect) => {
        const n = parseInt(rect.id, 10);
        if (isNaN(n) || rect.id.includes("-")) return;
        this._ringPill(rect, null);
        if (!activeGates.has(n)) {
          rect.setAttribute("fill", c.gateInactive);
          if (bridgeGates.has(n)) {
            rect.setAttribute("fill", bridgeFill);
            this._ringPill(rect, bridgeRing, this.BRIDGE_DASH);
            rect.setAttribute("data-gate-fill", bridgeFill);
            rect.setAttribute("data-gate-ring", bridgeRing);
            rect.setAttribute("data-gate-dash", this.BRIDGE_DASH);
          }
          return;
        }
        const style = pillStyle(n);
        rect.setAttribute("fill", style.fill);
        // Stamped so a hover can put the pill back: highlightGates cannot
        // re-derive which side activated a gate, and restoring it to one
        // "active" colour would flatten the whole drawing on first hover.
        rect.setAttribute("data-gate-fill", style.fill);
        if (style.ring) {
          this._ringPill(rect, style.ring);
          rect.setAttribute("data-gate-ring", style.ring);
        }
      });
      centersGroup.querySelectorAll("text[id]").forEach((text) => {
        const n = parseInt(text.id, 10);
        if (isNaN(n) || text.id.includes("-")) return;
        if (!activeGates.has(n)) {
          text.setAttribute("fill", c.gateTextInactive);
          if (bridges && bridgeGates.has(n)) {
            text.setAttribute("fill", c.bridgeInk);
            text.setAttribute("data-gate-fill", c.bridgeInk);
            text.setAttribute("font-weight", "700");
            text.setAttribute("data-gate-bold", "");
          }
          return;
        }
        const style = pillStyle(n);
        text.setAttribute("fill", style.text);
        text.setAttribute("data-gate-fill", style.text);
      });
    }

    // ── Variable arrows ──
    const vars = data.variables;
    if (vars) {
      const arrowDefs = [
        { key: "digestion", id: "arrow-digestion", side: "design" },
        { key: "environment", id: "arrow-environment", side: "design" },
        { key: "motivation", id: "arrow-motivation", side: "personality" },
        { key: "perspective", id: "arrow-perspective", side: "personality" },
      ];
      for (const def of arrowDefs) {
        const arrow = vars[def.key];
        const g = svg.getElementById(def.id);
        if (!g || !arrow) continue;
        const path = g.querySelector("path");
        if (!path) continue;
        path.setAttribute("fill", def.side === "design" ? c.channelDesign : c.channelPersonality);
        path.setAttribute("opacity", "0.7");
        // The asset centres each triangle on x = 7, so mirroring about it
        // (x → 14 − x) turns it in place at any size.
        if (arrow.direction === "left") {
          path.setAttribute("transform", "translate(14, 0) scale(-1, 1)");
        } else {
          path.removeAttribute("transform");
        }
      }
    }
  },

  // ── An uncertain birth time: a window, not a minute ───────────
  //
  // `item_tiers` measures every shape across the window. Two claims only:
  // certain (holds all window: the exact chart's colours) and likely (>60%:
  // --hd-likely). Everything else is drawn open, not in a third colour. No red
  // and no black: a window never says which side put a shape there.

  /** The two tiers a shape can be drawn in, strongest first. */
  TIERS: ["certain", "likely"],

  /** The tier a window-measured item is drawn in: `"certain"`, `"likely"`, or
   *  null for a shape left open. Duplicates GHD.chartInfo.drawnTier on purpose
   *  (chart-info.js is not loaded where this paints); birth-time-tiers.test.js
   *  holds the two to the same answers. */
  drawnTier(item) {
    if (!item || !item.present) return null;
    return item.tier === "certain" || item.tier === "likely" ? item.tier : null;
  },

  /** Apply one resolved `{fill, stroke, width, dash}` to an SVG element. */
  _applyTierStyle(el, style) {
    el.setAttribute("fill", style.fill);
    if (style.stroke) {
      el.setAttribute("stroke", style.stroke);
      el.setAttribute("stroke-width", String(style.width));
    } else {
      el.removeAttribute("stroke");
      el.removeAttribute("stroke-width");
    }
    if (style.dash) el.setAttribute("stroke-dasharray", style.dash);
    else el.removeAttribute("stroke-dasharray");
  },

  /**
   * Paint the bodygraph from a window's `item_tiers` (backend
   * birth_time/fence.py `_item_tier_payload`).
   *
   * Every shape the window settles is drawn in its own colour — the classic
   * defined/active colour for certain, the one grey for likely — and every
   * other shape is left open, so a reader can tell a channel that holds every
   * minute of the window from one that holds most of it, and either from one
   * that depends on the exact minute. Nothing here reads a line number, a
   * planetary activation or which side activated a gate: those are
   * minute-precise and never travel on this payload.
   *
   * A window that settles everything draws plain — the classic bodygraph with
   * no tier stamps. A remembered hour often buys that (Yuki's ±3 h clamps to
   * under four hours), and stamping a drawing that holds at every minute of its
   * window warns the reader about nothing.
   *
   * @param {HTMLElement} container
   * @param {Object} itemTiers — `{ centers: [], channels: [], hanging: [], gates: [] }`
   * @param {string} _namespace — the suffix paint() gives generated <defs>
   *   ids; kept so the two painters share one signature, unused here because
   *   nothing below mints a pattern
   */
  paintTiers(container, itemTiers, _namespace = "", { forceLight = false } = {}) {
    const svg = container.querySelector("svg");
    if (!svg) return;
    // An exact chart repainted in place as uncertain (edit, save, refresh)
    // keeps nothing of the ordinary paint: no Variable arrows, no side rings.
    this._setVariableArrowsHidden(svg, true);
    this._clearTierState(svg);
    this._resetGatePills(svg);
    const c = this.colors({ forceLight });
    const tiers = itemTiers || {};

    // A window that settled every shape it touched has no doubt to draw.
    const plain = Boolean(window.GHD && GHD.chartInfo && GHD.chartInfo.everyItemCertain(itemTiers));
    const stampTier = (el, tier) => {
      if (!plain && tier) el.setAttribute("data-tier", tier);
    };

    // ── Centers ──
    for (const center of tiers.centers || []) {
      const el = svg.getElementById(this.CENTER_ID_MAP[center.name]);
      if (!el) continue;
      const tier = this.drawnTier(center);
      let style;
      if (tier === "certain") style = { fill: c.centerDefined, stroke: c.centerStrokeDefined, width: 2 };
      else if (tier === "likely") style = { fill: c.likelyFill, stroke: c.likely, width: 2 };
      else style = { fill: c.centerUndefined, stroke: c.centerStrokeUndefined, width: 1.5 };
      this._applyTierStyle(el, { ...style, dash: "" });
      stampTier(el, tier);
    }

    this._resetChannels(svg, c);

    // ── Channels and half-channels, ONE paint per pair ──
    const rank = { likely: 1, certain: 2 };
    const byPair = new Map();
    const consider = (stroke) => {
      const key = [stroke.gate, stroke.partner].sort((a, b) => a - b).join("-");
      const held = byPair.get(key);
      if (held) {
        const better = rank[stroke.tier] - rank[held.tier];
        if (better < 0 || (better === 0 && !stroke.whole)) return;
      }
      byPair.set(key, stroke);
    };
    for (const channel of tiers.channels || []) {
      const tier = this.drawnTier(channel);
      if (tier) consider({ tier, gate: channel.gate_a, partner: channel.gate_b, whole: true });
    }
    for (const stub of tiers.hanging || []) {
      const tier = this.drawnTier(stub);
      if (tier) consider({ tier, gate: stub.gate, partner: stub.partner_gate, whole: false });
    }
    for (const stroke of byPair.values()) {
      const fill = stroke.tier === "certain" ? c.gateActive : c.likely;
      if (stroke.whole) {
        // A whole channel is stamped like an exact chart's, so a hovered row
        // lights it on a window-measured drawing too.
        const key = this.channelKey(stroke.gate, stroke.partner);
        const mark = (el) => {
          stampTier(el, stroke.tier);
          this._stampChannel(el, key);
        };
        this._paintChannel(svg, { gate_a: stroke.gate, gate_b: stroke.partner }, fill, fill, mark);
      } else {
        this._paintHangingGate(svg, stroke.gate, stroke.partner, fill, (el) => stampTier(el, stroke.tier));
      }
    }

    // ── Gate pills ──
    const byGate = new Map();
    for (const gate of tiers.gates || []) byGate.set(gate.gate, gate);
    const centersGroup = svg.getElementById("Centers");
    if (!centersGroup) return;
    const pillFill = (n) => {
      const tier = this.drawnTier(byGate.get(n));
      if (tier === "certain") return c.gateActive;
      if (tier === "likely") return c.likely;
      return c.gateInactive;
    };
    centersGroup.querySelectorAll("rect[id]").forEach((rect) => {
      const n = parseInt(rect.id, 10);
      if (isNaN(n) || rect.id.includes("-")) return;
      const fill = pillFill(n);
      this._applyTierStyle(rect, { fill, stroke: "", width: 1, dash: "" });
      // Stamped so highlightGates can put the pill back exactly as it was: a
      // hover cannot re-derive a tier. Kept even when `plain`.
      rect.setAttribute("data-tier-fill", fill);
      rect.setAttribute("data-tier-stroke", "");
      rect.setAttribute("data-tier-dash", "");
    });
    centersGroup.querySelectorAll("text[id]").forEach((text) => {
      const n = parseInt(text.id, 10);
      if (isNaN(n) || text.id.includes("-")) return;
      const fill = this.drawnTier(byGate.get(n)) ? c.gateTextActive : c.gateTextInactive;
      text.setAttribute("fill", fill);
      text.setAttribute("data-tier-fill", fill);
    });
  },

  /** Person-color palette for the connection chart, resolved from the group */
  connectionColors({ forceLight = false } = {}) {
    const { read, done } = this._tokenReader({ forceLight });
    const dark = !forceLight && this._isDark();
    const out = {
      m1: read("--m1", dark ? "#3987e5" : "#2a78d6"),
      m1Ink: read("--m1-ink", "#fff"),
      m2: read("--m2", dark ? "#c98500" : "#eda100"),
      m2Ink: read("--m2-ink", dark ? "#141210" : "#1a1613"),
      accent: read("--accent", dark ? "#e07a3e" : "#c4622d"),
      surface: read("--surface", dark ? "#201e1b" : "#ffffff"),
    };
    done();
    return out;
  },

  _parseHex(color) {
    const m = /^#([0-9a-f]{6})$/i.exec((color || "").trim());
    if (!m) return null;
    return [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16));
  },

  /** `weight` of `color` blended over `base` — a dependency-free color-mix. */
  _tint(color, base, weight) {
    const a = this._parseHex(color);
    const b = this._parseHex(base);
    if (!a || !b) return color;
    const mixed = a.map((v, i) => Math.round(v * weight + b[i] * (1 - weight)));
    return `rgb(${mixed[0]},${mixed[1]},${mixed[2]})`;
  },

  /** Paint the composite connection chart for two people.
   *  Every half-channel and pill is filled by who holds its gate (--m1, --m2, or
   *  a stripe for both); centers defined only together are accent-tinted and
   *  dashed. `data` is the compare payload (needs `people[].gates`). Elements are
   *  stamped `data-cmp-holders` / `data-cmp-g` for CSS focus. `forceLight` and
   *  `connectionColors` are for the PDF export. */
  paintConnection(container, data, namespace = "", { forceLight = false, connectionColors = null } = {}) {
    const svg = container.querySelector("svg");
    if (!svg) return;
    // A person chart reused as a connection chart must not keep the rings its
    // gate pills wore: here a colour means which PERSON holds a gate, not which
    // side of one person activated it.
    this._resetGatePills(svg);
    // Nor its arrows. Variable is one person's — two people have four arrows
    // each and no shared pair of them — so a composite draws none, and a host
    // that was somebody's own chart a moment ago must lose the ones it has.
    this._setVariableArrowsHidden(svg, true);
    const c = this.colors({ forceLight });
    const duo = connectionColors || this.connectionColors({ forceLight });
    const dark = !forceLight && this._isDark();
    const composite = data.composite || {};
    const people = data.people || [];
    const gateSets = [new Set(people[0]?.gates || []), new Set(people[1]?.gates || [])];
    // A pair with an uncertain birth time is measured across its joint window
    // (backend birth_time/pair.py): only what `item_tiers` vouches for is drawn,
    // certain in the holders' colours, likely in the one grey paintTiers uses.
    const tiers = composite.item_tiers || null;
    this._clearTierState(svg);
    const plainTiers = Boolean(tiers && window.GHD && GHD.chartInfo && GHD.chartInfo.everyItemCertain(tiers));
    const stampTier = (el, tier) => {
      if (tiers && !plainTiers && tier) el.setAttribute("data-tier", tier);
    };
    const gateTier = new Map();
    const gateHolders = new Map();
    for (const gate of tiers?.gates || []) {
      const tier = this.drawnTier(gate);
      if (!tier) continue;
      gateTier.set(gate.gate, tier);
      gateHolders.set(gate.gate, gate.holders || []);
    }
    const activeGates = tiers ? new Set(gateTier.keys()) : new Set(composite.gates || []);

    const patId = `duo-stripe${namespace}`;
    this._ensureStripePattern(svg, patId, duo.m1, duo.m2);

    // Bitmask of who holds a gate: 1 = person 1, 2 = person 2, 3 = both.
    // Defaults to 3 defensively — every composite-active gate has a holder.
    const maskOf = (gate) => {
      if (tiers) {
        const holders = gateHolders.get(gate) || [];
        return (holders.includes(0) ? 1 : 0) + (holders.includes(1) ? 2 : 0) || 3;
      }
      return (gateSets[0].has(gate) ? 1 : 0) + (gateSets[1].has(gate) ? 2 : 0) || 3;
    };
    const fillFor = (mask) => (mask === 3 ? `url(#${patId})` : mask === 2 ? duo.m2 : duo.m1);
    const holdersFor = (mask) => (mask === 3 ? "m1 m2" : mask === 2 ? "m2" : "m1");
    const gateFill = (gate) => (gateTier.get(gate) === "likely" ? c.likely : fillFor(maskOf(gate)));

    // ── Centers (by relationship dynamic) ──
    const slotByName = {};
    people.forEach((p, i) => {
      slotByName[p.name] = i === 0 ? "m1" : "m2";
    });
    const dynByCenter = {};
    (data.center_dynamics || []).forEach((cd) => {
      dynByCenter[cd.name] = cd;
    });
    const centerTier = {};
    for (const center of tiers?.centers || []) centerTier[center.name] = center;
    for (const name of Object.keys(this.CENTER_ID_MAP)) {
      const el = svg.getElementById(this.CENTER_ID_MAP[name]);
      if (!el) continue;
      let cd = dynByCenter[name] || { dynamic: "open", defined_by: [] };
      el.removeAttribute("stroke-dasharray");
      if (tiers) {
        // Who defines a center is drawn only when the window settles it; a
        // center that is surely defined by someone unsettled is drawn plain.
        const item = centerTier[name];
        const tier = this.drawnTier(item);
        stampTier(el, item?.dynamic_tier === "certain" ? "certain" : tier);
        if (item?.dynamic_tier !== "certain") {
          const style =
            tier === "certain"
              ? { fill: c.centerDefined, stroke: c.centerStrokeDefined, width: 2 }
              : tier === "likely"
                ? { fill: c.likelyFill, stroke: c.likely, width: 2 }
                : { fill: c.centerUndefined, stroke: c.centerStrokeUndefined, width: 1.5 };
          this._applyTierStyle(el, { ...style, dash: "" });
          el.removeAttribute("data-cmp-holders");
          continue;
        }
        cd = { ...cd, dynamic: item.dynamic };
      }
      if (cd.dynamic === "companionship") {
        el.setAttribute("fill", c.centerDefined);
        el.setAttribute("stroke", c.centerStrokeDefined);
        el.setAttribute("stroke-width", "2");
        el.setAttribute("data-cmp-holders", "m1 m2");
      } else if (cd.dynamic === "dominant") {
        const slot = slotByName[(cd.defined_by || [])[0]] || "m1";
        const color = slot === "m2" ? duo.m2 : duo.m1;
        el.setAttribute("fill", this._tint(color, duo.surface, dark ? 0.3 : 0.15));
        el.setAttribute("stroke", this._tint(color, duo.surface, dark ? 0.75 : 0.6));
        el.setAttribute("stroke-width", "2");
        el.setAttribute("data-cmp-holders", slot);
      } else if (cd.dynamic === "electromagnetic") {
        el.setAttribute("fill", this._tint(duo.accent, duo.surface, dark ? 0.32 : 0.16));
        el.setAttribute("stroke", this._tint(duo.accent, duo.surface, dark ? 0.9 : 0.75));
        el.setAttribute("stroke-width", "2");
        el.setAttribute("stroke-dasharray", "5 3");
        el.setAttribute("data-cmp-holders", "m1 m2");
      } else {
        el.setAttribute("fill", c.centerUndefined);
        el.setAttribute("stroke", c.centerStrokeUndefined);
        el.setAttribute("stroke-width", "1.5");
        el.removeAttribute("data-cmp-holders");
      }
    }

    this._resetChannels(svg, c);

    // ── Channels + hanging gates, colored by holder ──
    if (tiers) {
      this._paintConnectionTieredStrokes(svg, tiers, { gateFill, maskOf, holdersFor, stampTier, likely: c.likely });
    }
    for (const ch of tiers ? [] : composite.defined_channels || []) {
      const key = `${ch.gate_a}-${ch.gate_b}`;
      const markEl = (el, gates) => {
        let mask = 0;
        gates.forEach((g) => {
          mask |= maskOf(g);
        });
        el.setAttribute("data-cmp-holders", holdersFor(mask));
        el.setAttribute("data-cmp-g", gates.join(" "));
        el.setAttribute("data-cmp-ch", key);
      };
      this._paintChannel(svg, ch, fillFor(maskOf(ch.gate_a)), fillFor(maskOf(ch.gate_b)), markEl);
    }

    for (const hg of tiers ? [] : composite.hanging_gates || []) {
      const mask = maskOf(hg.gate);
      this._paintHangingGate(svg, hg.gate, hg.partner_gate, fillFor(mask), (el) => {
        el.setAttribute("data-cmp-holders", holdersFor(mask));
        el.setAttribute("data-cmp-g", String(hg.gate));
      });
    }

    // ── Gate pills in holder colors ──
    const centersGroup = svg.getElementById("Centers");
    if (centersGroup) {
      centersGroup.querySelectorAll("rect[id]").forEach((rect) => {
        const n = parseInt(rect.id, 10);
        if (isNaN(n) || rect.id.includes("-")) return;
        if (!activeGates.has(n)) {
          rect.setAttribute("fill", c.gateInactive);
          rect.removeAttribute("data-cmp-holders");
          return;
        }
        const mask = maskOf(n);
        rect.setAttribute("fill", gateFill(n));
        rect.setAttribute("data-cmp-holders", holdersFor(mask));
        stampTier(rect, gateTier.get(n));
      });
      centersGroup.querySelectorAll("text[id]").forEach((text) => {
        const n = parseInt(text.id, 10);
        if (isNaN(n) || text.id.includes("-")) return;
        const clearHalo = () => {
          text.removeAttribute("stroke");
          text.removeAttribute("stroke-width");
          text.removeAttribute("paint-order");
        };
        if (!activeGates.has(n)) {
          text.setAttribute("fill", c.gateTextInactive);
          text.removeAttribute("data-cmp-holders");
          clearHalo();
          return;
        }
        const mask = maskOf(n);
        if (gateTier.get(n) === "likely") {
          text.setAttribute("fill", c.gateTextActive);
          clearHalo();
        } else if (mask === 3) {
          // White numeral with a soft dark halo stays readable across the
          // blue/amber stripes of a both-held pill.
          text.setAttribute("fill", c.gateTextBoth);
          text.setAttribute("stroke", c.gateTextBothHalo);
          text.setAttribute("stroke-width", "2");
          text.setAttribute("paint-order", "stroke");
        } else {
          text.setAttribute("fill", mask === 2 ? duo.m2Ink : duo.m1Ink);
          clearHalo();
        }
        text.setAttribute("data-cmp-holders", holdersFor(mask));
      });
    }
  },

  /** The composite's channels and half-channels from a joint window's
   *  `item_tiers`: one paint per pair, the strongest claim winning, as in
   *  paintTiers. A likely stroke is grey on both halves: the window vouches
   *  for the shape, not for whose gate completes it. */
  _paintConnectionTieredStrokes(svg, tiers, { gateFill, maskOf, holdersFor, stampTier, likely }) {
    const rank = { likely: 1, certain: 2 };
    const byPair = new Map();
    const consider = (stroke) => {
      const key = [stroke.gate, stroke.partner].sort((a, b) => a - b).join("-");
      const held = byPair.get(key);
      if (held) {
        const better = rank[stroke.tier] - rank[held.tier];
        if (better < 0 || (better === 0 && !stroke.whole)) return;
      }
      byPair.set(key, stroke);
    };
    for (const channel of tiers.channels || []) {
      const tier = this.drawnTier(channel);
      if (tier) consider({ tier, gate: channel.gate_a, partner: channel.gate_b, whole: true });
    }
    for (const stub of tiers.hanging || []) {
      const tier = this.drawnTier(stub);
      if (tier) consider({ tier, gate: stub.gate, partner: stub.partner_gate, whole: false });
    }
    for (const stroke of byPair.values()) {
      const fill = (gate) => (stroke.tier === "likely" ? likely : gateFill(gate));
      if (stroke.whole) {
        const key = `${Math.min(stroke.gate, stroke.partner)}-${Math.max(stroke.gate, stroke.partner)}`;
        const mark = (el, gates) => {
          let mask = 0;
          gates.forEach((g) => {
            mask |= maskOf(g);
          });
          el.setAttribute("data-cmp-holders", holdersFor(mask));
          el.setAttribute("data-cmp-g", gates.join(" "));
          el.setAttribute("data-cmp-ch", key);
          stampTier(el, stroke.tier);
        };
        this._paintChannel(
          svg,
          { gate_a: stroke.gate, gate_b: stroke.partner },
          fill(stroke.gate),
          fill(stroke.partner),
          mark,
        );
      } else {
        this._paintHangingGate(svg, stroke.gate, stroke.partner, fill(stroke.gate), (el) => {
          el.setAttribute("data-cmp-holders", holdersFor(maskOf(stroke.gate)));
          el.setAttribute("data-cmp-g", String(stroke.gate));
          stampTier(el, stroke.tier);
        });
      }
    }
  },

  // ── The sky over a chart: the DETAILED view ───────────────────────────
  //
  // --hd-sky over the reader's already-painted chart, in four marks
  // (backend/engine/transit_overlay.py): direct (solid sky half; a center it
  // switches on gets a dashed ring and KEEPS the open fill), background
  // (hatched: everyone has it), amplified (a dot on a shape the reader already
  // holds, never recoloured), and hanging (a sky stub, derived from
  // `sky_gates`). Elements are stamped `data-sky`, `data-sky-g`, `data-sky-ch`.

  /** The dot that marks an amplified channel, in SVG user units, so a 210px
   *  chat card scales it down. One size on every channel on purpose; it sits on
   *  the channel's centre line at the join, so any overhang is symmetric.
   *  SKY_DOT_INSET places the fallback badge on the pill's top-right rim. */
  SKY_DOT_R: "5",
  SKY_DOT_PILL_R: "3",
  SKY_DOT_INSET: 1.5,

  /** The key under the DETAILED sky drawing (paintSky), as data:
   *  `[swatchClass, messageKey, English, group]`. Grouped ("" = the reader's own
   *  chart, "channels", "centers") so a channel word never labels a center mark.
   *  "Direct", not "Sky", for the solid half: all three marks are the sky.
   *  Exported, not rendered: each surface owns its own width. */
  SKY_KEY_ITEMS: [
    ["sw-personality", "chart.personality_header", "Personality", ""],
    ["sw-design", "chart.design_header", "Design", ""],
    ["sw-both", "chart.legend_both", "Both", ""],
    ["sw-sky", "chart.sky_key_direct", "Direct", "channels"],
    ["sw-sky-bg", "chart.sky_key_background", "Background", "channels"],
    ["sw-sky-amp", "chart.sky_key_amplified", "Amplified", "channels"],
    // The fourth channel mark, and the one that names something the drawing
    // used to leave out: a planet standing in a gate that completes nothing.
    ["sw-sky-hanging", "chart.sky_key_hanging", "Hanging gate", "channels"],
    ["sw-sky-center", "chart.sky_key_center", "Activated by the sky", "centers"],
  ],

  /** The heading each SKY_KEY_ITEMS group is drawn under, where a surface has
   *  the width for headings. The reader's own inks carry no heading. */
  SKY_KEY_GROUPS: {
    channels: ["chart.sky_key_group_channels", "The sky — channels"],
    centers: ["chart.sky_key_group_centers", "The sky — centers"],
  },

  /** The items of one SKY_KEY_ITEMS group, in order. */
  skyKeyGroup(group) {
    return this.SKY_KEY_ITEMS.filter((item) => item[3] === group);
  },

  /** The key under the DEFAULT sky drawing (paintSkyCombined), in the same
   *  `[swatchClass, messageKey, English, group]` shape, with no groups.
   *  `chart.sky_key_shared`, not `chart.legend_both`, so a locale can word
   *  "you and the sky" differently from "both consciousnesses". */
  SKY_COMBINED_KEY_ITEMS: [
    ["sw-you", "chart.sky_key_you", "You", ""],
    ["sw-sky", "chart.sky_key_sky", "The sky", ""],
    ["sw-sky-shared", "chart.sky_key_shared", "Both", ""],
    ["sw-sky-lit", "chart.sky_key_lit", "Lit by the sky", ""],
  ],

  /** The same key for a pair's composite drawn with `pairInk: true`. The first
   *  item's words take `{a}` and `{b}`, the two first names. */
  SKY_PAIR_KEY_ITEMS: [
    ["sw-sky-pair", "chart.sky_key_pair", "{a} & {b}", ""],
    ["sw-sky", "chart.sky_key_sky", "The sky", ""],
    ["sw-sky-pair-shared", "chart.sky_key_pair_shared", "Both", ""],
    ["sw-sky-lit", "chart.sky_key_lit", "Lit by the sky", ""],
  ],

  /** Every gate the sky HOLDS at the drawn instant, as a Set —
   *  `overlay.sky_gates`. Holds, not owns: a gate can be the reader's too, so
   *  this is never the "paint it blue" set. Without the key (older payloads) it
   *  falls back to the overlay channels' gates, which misses loose ones. */
  skyGates(overlay) {
    const ov = overlay || {};
    const gates = new Set(ov.sky_gates || []);
    if (ov.sky_gates) return gates;
    for (const channels of [ov.direct || [], ov.background || [], ov.amplified || []]) {
      for (const ch of channels) for (const gate of ch.sky_gates || []) gates.add(gate);
    }
    return gates;
  },

  /** Strip everything paintSky() stamped, so the next paint of this SVG — a
   *  theme flip, a scrubbed timeline, a plain chart — starts weather-free.
   *  What the sky added (`data-sky-own`) is removed; attributes are removed as
   *  attributes, as in _clearTierState. */
  _clearSkyState(svg) {
    if (!svg || typeof svg.querySelectorAll !== "function") return;
    for (const own of svg.querySelectorAll("[data-sky-own]")) own.remove();
    for (const el of svg.querySelectorAll("[data-sky]")) {
      el.removeAttribute("data-sky");
      el.removeAttribute("data-sky-g");
      el.removeAttribute("data-sky-ch");
      el.removeAttribute("stroke-dasharray");
    }
  },

  /** Drop the midpoint clip _paintHangingGate put on a channel group.
   *  A direct channel is half the reader's and half the sky's, and the clip
   *  that showed only the reader's stub would hide the sky's half completely. */
  _unclipChannel(svg, gateA, gateB) {
    const group = svg.getElementById(`${gateA}-${gateB}`) || svg.getElementById(`${gateB}-${gateA}`);
    if (group) group.removeAttribute("clip-path");
  },

  /**
   * Paint the sky over a natal chart.
   *
   * @param {HTMLElement} container — holds the loaded bodygraph SVG
   * @param {Object} natalData — the paint block paint() takes (the contract's
   *   `overlay.chart`: centers, defined_channels with a consciousness per gate,
   *   hanging_gates, gates)
   * @param {Object} overlay — the contract's `overlay`: direct, background,
   *   amplified, centers, and `sky_gates` — every gate the sky holds, which is
   *   the only place the loose ones are named (see skyGates)
   * @param {string} namespace — suffix for generated <defs> ids, so two
   *   drawings on one page never share a pattern
   * @param {Object} [options] — `forceLight: true` paints the light palette
   */
  paintSky(container, natalData, overlay, namespace = "", { forceLight = false } = {}) {
    const svg = container.querySelector("svg");
    if (!svg) return;
    // The natal chart first and whole, so tiers, resets, the gate pills and the
    // consciousness rings all behave exactly as they do anywhere else. (paint()
    // clears any previous sky through _resetGatePills.)
    this.paint(container, natalData, namespace, { forceLight });
    // Variable is one person's, read off the design Sun to the minute. A sky
    // drawing is two things at once, like a composite, so it draws no arrows.
    this._setVariableArrowsHidden(svg, true);
    const c = this.colors({ forceLight });
    // --surface is connectionColors()'s to read: it is the page's ground, not a
    // chart colour, and COLOR_TOKENS carries only what the chart is painted in.
    const surface = this.connectionColors({ forceLight }).surface;
    const ov = overlay || {};
    // The reader's own gates, read once: the loose sky gates below and the
    // pills at the end are both decided by subtracting them from the sky's.
    const readerGates = new Set(natalData.gates || []);

    // A center has neither gates nor a channel of its own to name — several may
    // define it — so it is stamped with the mark alone rather than with empty
    // attributes a selector would never match.
    const stamp = (el, mark, gates, ch) => {
      el.setAttribute("data-sky", mark);
      if (gates.length) el.setAttribute("data-sky-g", gates.join(" "));
      if (ch) el.setAttribute("data-sky-ch", ch);
    };

    // ── direct: the sky's half solid, the reader's half untouched ──
    for (const ch of ov.direct || []) {
      const key = `${ch.gate_a}-${ch.gate_b}`;
      this._unclipChannel(svg, ch.gate_a, ch.gate_b);
      for (const gate of ch.sky_gates || []) {
        this._fillGatePaths(svg, gate, c.sky, (el) => stamp(el, "direct", [gate], key));
      }
    }

    // ── background: hatched end to end, outlined so the stripes read as one shape ──
    const background = ov.background || [];
    let hatch = "";
    if (background.length) {
      const hatchId = `sky-hatch${namespace}`;
      // The same 45° tile the two-consciousness stripe uses, with nothing in
      // the gaps — hatching, not a second solid.
      this._ensureStripePattern(svg, hatchId, c.skyHatch, "none");
      const pattern = svg.querySelector(`[id="${hatchId}"]`);
      if (pattern) pattern.setAttribute("data-sky-own", "1");
      hatch = `url(#${hatchId})`;
    }
    for (const ch of background) {
      const key = `${ch.gate_a}-${ch.gate_b}`;
      this._paintChannel(svg, ch, hatch, hatch, (el, gates) => {
        el.setAttribute("stroke", c.sky);
        el.setAttribute("stroke-width", "1");
        stamp(el, "background", gates, key);
      });
    }

    // ── loose: a planet standing in a gate that completes nothing ──
    //
    // The overlay names only gates that DID something, so this is derived. A
    // gate the reader also holds keeps their stub (marked by the amplified dot
    // below). No channel key is stamped: a stub is not a channel.
    const engaged = new Set();
    for (const channels of [ov.direct || [], ov.background || [], ov.amplified || []]) {
      for (const ch of channels) for (const gate of ch.sky_gates || []) engaged.add(gate);
    }
    // The reader's own hanging gates, gate → every partner it hangs toward: the
    // one route to the geometry of a stub that is already on the drawing.
    const hangingPartners = new Map();
    for (const hg of natalData.hanging_gates || []) {
      if (!hangingPartners.has(hg.gate)) hangingPartners.set(hg.gate, []);
      hangingPartners.get(hg.gate).push(hg.partner_gate);
    }
    const loose = [];
    const occupied = [];
    for (const gate of this.skyGates(ov)) {
      if (engaged.has(gate)) continue;
      if (!readerGates.has(gate)) loose.push(gate);
      else if (hangingPartners.has(gate)) occupied.push(gate);
    }
    for (const gate of loose) {
      // Every partner, which matters only for the four Integration gates: they
      // have three each and one leg between them, so the three stubs are three
      // paints of the same shape in the same ink.
      const partners = this.GATE_PARTNERS[gate] || [];
      for (let i = 0; i < partners.length; i += 1) {
        this._paintHangingGate(svg, gate, partners[i], c.sky, (el) => stamp(el, "hanging", [gate], ""));
      }
    }

    // ── Centers the sky defines ──
    //
    // Dashed, never filled: the fill says "yours". One weight for direct and
    // background alike; `by` is still stamped in `data-sky`.
    for (const center of ov.centers || []) {
      const el = svg.getElementById(this.CENTER_ID_MAP[center.name]);
      if (!el) continue;
      el.setAttribute("stroke", c.sky);
      el.setAttribute("stroke-width", this.CENTER_SIDE_WIDTH);
      el.setAttribute("stroke-dasharray", "5 3");
      stamp(el, center.by === "direct" ? "direct" : "background", [], "");
    }

    // ── Gate pills, and the amplified dots ──
    const centersGroup = svg.getElementById("Centers");
    if (!centersGroup) return;
    // One rule: a pill the READER does not hold, that the SKY does, wears the
    // sky, with a memo for highlightGates to restore. The reader's own gates
    // are subtracted (they can overlap `background`): their ink always wins.
    // A Map so each pill is stamped with the mark that put it there.
    const skyPills = new Map();
    for (const [kind, channels] of [
      ["direct", ov.direct || []],
      ["background", ov.background || []],
    ]) {
      for (const ch of channels) {
        for (const gate of ch.sky_gates || []) {
          if (!readerGates.has(gate) && !skyPills.has(gate)) skyPills.set(gate, kind);
        }
      }
    }
    // And the loose gates, whose stub above would otherwise run out of a pill
    for (const gate of loose) if (!skyPills.has(gate)) skyPills.set(gate, "hanging");
    const eachPill = (tag, fn) => {
      centersGroup.querySelectorAll(`${tag}[id]`).forEach((el) => {
        const n = parseInt(el.id, 10);
        if (isNaN(n) || el.id.includes("-")) return;
        fn(el, n);
      });
    };
    const rectById = new Map();
    eachPill("rect", (rect, n) => {
      rectById.set(n, rect);
      if (!skyPills.has(n)) return;
      rect.setAttribute("fill", c.sky);
      rect.setAttribute("data-gate-fill", c.sky);
      stamp(rect, skyPills.get(n), [n], "");
    });
    eachPill("text", (text, n) => {
      if (!skyPills.has(n)) return;
      text.setAttribute("fill", c.gateTextActive);
      text.setAttribute("data-gate-fill", c.gateTextActive);
      stamp(text, skyPills.get(n), [n], "");
    });

    // An amplified shape keeps the colours it already has; the planet is a dot
    // ringed in the page's ground, pointer-events off. One dot per shape, not
    // per sky gate. It goes INSIDE the channel's group, whose own coordinate
    // space it was measured in, and last so it sits on top.
    const dotAt = (parent, cx, cy, r, gates, key) => {
      const dot = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      dot.setAttribute("cx", String(cx));
      dot.setAttribute("cy", String(cy));
      dot.setAttribute("r", r);
      dot.setAttribute("fill", c.sky);
      dot.setAttribute("stroke", surface);
      dot.setAttribute("stroke-width", "1");
      dot.setAttribute("pointer-events", "none");
      dot.setAttribute("data-sky-own", "1");
      stamp(dot, "amplified", gates, key);
      parent.appendChild(dot);
    };

    // Where there is no geometry to measure, the mark falls back to a BADGE on
    // the pill's top-right rim, clear of the gate number: the integration
    // channels (one shared group) and any unrendered chart.
    const badgeOnPill = (gate, key) => {
      const rect = rectById.get(gate);
      if (!rect) return;
      const x = parseFloat(rect.getAttribute("x"));
      const y = parseFloat(rect.getAttribute("y"));
      const w = parseFloat(rect.getAttribute("width"));
      const h = parseFloat(rect.getAttribute("height"));
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(w) || !Number.isFinite(h)) return;
      dotAt(
        rect.parentNode || centersGroup,
        x + w - this.SKY_DOT_INSET,
        y + this.SKY_DOT_INSET,
        this.SKY_DOT_PILL_R,
        [gate],
        key,
      );
    };

    for (const ch of ov.amplified || []) {
      const key = `${ch.gate_a}-${ch.gate_b}`;
      const group = svg.getElementById(key) || svg.getElementById(`${ch.gate_b}-${ch.gate_a}`);
      const mid = this._channelMidpoint(svg, group, ch.gate_a, ch.gate_b);
      if (mid) {
        dotAt(group, mid.x, mid.y, this.SKY_DOT_R, [ch.gate_a, ch.gate_b], key);
        continue;
      }
      for (const gate of ch.sky_gates || []) badgeOnPill(gate, key);
    }

    // …and a planet standing in one of the reader's own hanging gates, which
    // no overlay row names. The dot goes on the drawn half, not the channel's
    // middle (the clip seam). No channel key: there is no channel.
    for (const gate of occupied) {
      const partners = hangingPartners.get(gate) || [];
      let marked = false;
      for (let i = 0; i < partners.length && !marked; i += 1) {
        const partner = partners[i];
        const group = svg.getElementById(`${gate}-${partner}`) || svg.getElementById(`${partner}-${gate}`);
        const mid = group ? this._stubMidpoint(svg, group, gate, partner) : null;
        if (!mid) continue;
        dotAt(group, mid.x, mid.y, this.SKY_DOT_R, [gate], "");
        marked = true;
      }
      if (!marked) badgeOnPill(gate, "");
    }
  },

  /** The middle of the half a stub draws, in its channel group's coordinates. */
  _stubMidpoint(svg, group, gate, partner) {
    const fixed = this._geometryMidpoint(svg.getElementById(`gate-${gate}`));
    if (fixed) return fixed;
    const join = this._channelMidpoint(svg, group, gate, partner);
    if (!join) return null;
    const leg = this._gateCenterInGroup(svg, gate, group);
    if (!leg) return null;
    return { x: (join.x + leg.x) / 2, y: (join.y + leg.y) / 2 };
  },

  // ── The sky over a chart: the DEFAULT view ────────────────────────────
  //
  // One question in colour alone: who holds this gate, you, the sky, or both?
  // paintConnection's grammar, with the sky as the second person. No Design/
  // Personality, no hatch, no bead (those live in Detailed). A center the sky
  // defines is FILLED in sky tint and dashed (docs/internal/decisions.md).

  /**
   * Paint the sky and the reader as one two-ink drawing.
   *
   * One rule produces all of it: A LEG, A PILL AND A CENTRE ARE FILLED BY WHO
   * HOLDS THEM. The reader's gates are `--m2`, the sky's `--hd-sky`, a gate
   * both hold is the two striped, and a centre only the sky defines is that
   * blue tinted over the surface, dashed. Amber against blue because `--m2` is
   * what the pairs and group charts already spend, and it is the pairing that
   * survives every kind of colour blindness.
   *
   * The same arguments as paintSky, so a surface swaps one name for the other
   * and nothing else: `natalData` is paint()'s block (the contract's
   * `overlay.chart`) and `overlay` the contract's overlay, of which this reads
   * `sky_gates` and `centers` and nothing else — the three channel lists say
   * what the sky DID, and this drawing does not ask.
   *
   * @param {HTMLElement} container — holds the loaded bodygraph SVG
   * @param {Object} natalData — centers, defined_channels, hanging_gates, gates
   * @param {Object} overlay — the contract's `overlay`
   * @param {string} namespace — suffix for generated <defs> ids, so two
   *   drawings on one page never share a pattern
   * @param {Object} [options] — `forceLight: true` paints the light palette;
   *   `pairInk: true` paints a pair's composite as the reader in the chart's own
   *   terracotta, since `--m2` is the second person's colour on the pair page
   */
  paintSkyCombined(container, natalData, overlay, namespace = "", { forceLight = false, pairInk = false } = {}) {
    const svg = container.querySelector("svg");
    if (!svg) return;
    const natal = natalData || {};
    const ov = overlay || {};
    // A chart measured across a birth-time WINDOW arrives as `item_tiers` and
    // carries no gates, and this view can only say who holds a gate, so the
    // detailed painter draws it instead.
    if (natal.item_tiers) return this.paintSky(container, natal, ov, namespace, { forceLight });
    // The housekeeping paint() and paintSky() establish, in their order. The
    // Variable arrows go: a composite has no single set of them.
    this._clearTierState(svg);
    this._setVariableArrowsHidden(svg, true);
    this._resetGatePills(svg);
    const c = this.colors({ forceLight });
    const duo = this.connectionColors({ forceLight });
    const dark = !forceLight && this._isDark();
    const readerFill = pairInk ? c.gateActive : duo.m2;
    const readerInk = pairInk ? c.gateTextActive : duo.m2Ink;

    const youGates = new Set(natal.gates || []);
    const skyGates = this.skyGates(ov);

    // paintConnection's own bitmask, with the sky in person 2's seat: 1 the
    // reader, 2 the sky, 3 both. The 0 is this painter's alone — the far end of
    // a stub, a gate nobody holds, which a connection chart never has to draw.
    const maskOf = (gate) => (youGates.has(gate) ? 1 : 0) + (skyGates.has(gate) ? 2 : 0);
    const patId = `sky-duo-stripe${namespace}`;
    this._ensureStripePattern(svg, patId, readerFill, c.sky);
    // Marked as the sky's own, so the next clear REMOVES it rather than leaving
    // a pattern in the defs that is also the previous theme's colours.
    const pattern = svg.querySelector(`[id="${patId}"]`);
    if (pattern) pattern.setAttribute("data-sky-own", "1");
    const fillOf = (mask) => (mask === 3 ? `url(#${patId})` : mask === 2 ? c.sky : readerFill);
    const holderOf = (mask) => (mask === 3 ? "both" : mask === 2 ? "sky" : "you");
    // Stamped in the `data-sky` family, like the detailed painter — which is
    // what lets _clearSkyState and highlightSkyChannel reach it.
    const stamp = (el, mask, gates, ch) => {
      el.setAttribute("data-sky", holderOf(mask));
      if (gates.length) el.setAttribute("data-sky-g", gates.join(" "));
      if (ch) el.setAttribute("data-sky-ch", ch);
    };

    // ── Centres ──
    //
    // The reader's own take the warm fill and the PLAIN defined stroke, no
    // consciousness ring: this view's key cannot explain one. Only a centre
    // the reader does NOT define can be the sky's.
    const definedCenters = {};
    for (const center of natal.centers || []) definedCenters[center.name] = Boolean(center.defined);
    const litCenters = new Set();
    for (const center of ov.centers || []) if (!definedCenters[center.name]) litCenters.add(center.name);
    // Every centre, not only the ones the payload names: this painter replaces
    // paint() rather than layering over it, so a centre nobody mentions has to
    // be reset here or it keeps whatever the previous drawing left on it.
    for (const name in this.CENTER_ID_MAP) {
      const el = svg.getElementById(this.CENTER_ID_MAP[name]);
      if (!el) continue;
      el.removeAttribute("stroke-dasharray");
      if (definedCenters[name]) {
        el.setAttribute("fill", c.centerDefined);
        el.setAttribute("stroke", c.centerStrokeDefined);
        el.setAttribute("stroke-width", "2");
      } else if (litCenters.has(name)) {
        el.setAttribute("fill", this._tint(c.sky, duo.surface, dark ? 0.3 : 0.18));
        el.setAttribute("stroke", c.sky);
        el.setAttribute("stroke-width", this.CENTER_SIDE_WIDTH);
        el.setAttribute("stroke-dasharray", "5 3");
        // Stamped as the sky's, like every other shape it holds. A centre has
        // no gates and no channel of its own to name — several may define it.
        stamp(el, 2, [], "");
      } else {
        el.setAttribute("fill", c.centerUndefined);
        el.setAttribute("stroke", c.centerStrokeUndefined);
        el.setAttribute("stroke-width", "1.5");
      }
    }

    this._resetChannels(svg, c);

    // ── The pairs to draw ──
    //
    // Derived, not read off the overlay (which names only gates that did
    // something): the reader's channels, their stubs, and every partner of
    // every sky gate, deduplicated on the unordered pair.
    const pairs = new Map();
    const consider = (a, b) => {
      const ga = Number(a);
      const gb = Number(b);
      if (!Number.isFinite(ga) || !Number.isFinite(gb) || ga === gb) return;
      const key = this.channelKey(ga, gb);
      if (!pairs.has(key)) pairs.set(key, ga < gb ? [ga, gb] : [gb, ga]);
    };
    for (const ch of natal.defined_channels || []) consider(ch.gate_a, ch.gate_b);
    for (const hg of natal.hanging_gates || []) consider(hg.gate, hg.partner_gate);
    for (const gate of skyGates) {
      const partners = this.GATE_PARTNERS[gate] || [];
      for (let i = 0; i < partners.length; i += 1) consider(gate, partners[i]);
    }

    // ── The legs ──
    //
    // Filled by GATE, never by channel: the four shared legs (10/20/34/57) are
    // painted several times, and only a per-gate answer makes order irrelevant.
    for (const pair of pairs.values()) {
      const gateA = pair[0];
      const gateB = pair[1];
      const maskA = maskOf(gateA);
      const maskB = maskOf(gateB);
      const key = this.channelKey(gateA, gateB);
      if (maskA && maskB) {
        this._paintChannel(svg, { gate_a: gateA, gate_b: gateB }, fillOf(maskA), fillOf(maskB), (el, gates) => {
          let mask = 0;
          for (let i = 0; i < gates.length; i += 1) mask |= maskOf(gates[i]);
          stamp(el, mask, gates, key);
          // The channel stamp too, so a hovered row in the channel list lights
          // its channel on this drawing the way it does on every other.
          this._stampChannel(el, key);
        });
        continue;
      }
      if (!maskA && !maskB) continue;
      // A stub, and no channel key with it: half a channel is not a channel,
      // and a list row that names one must select nothing rather than a shape
      // that is only half there.
      const held = maskA ? gateA : gateB;
      const partner = maskA ? gateB : gateA;
      const mask = maskOf(held);
      this._paintHangingGate(svg, held, partner, fillOf(mask), (el) => stamp(el, mask, [held], ""));
    }

    // ── Gate pills ──
    //
    // The same rule, so a pill and its leg never disagree. `data-gate-fill` is
    // the memo highlightGates restores after a hover.
    const centersGroup = svg.getElementById("Centers");
    if (!centersGroup) return;
    const eachPill = (tag, fn) => {
      centersGroup.querySelectorAll(`${tag}[id]`).forEach((el) => {
        const n = parseInt(el.id, 10);
        if (isNaN(n) || el.id.includes("-")) return;
        fn(el, n);
      });
    };
    eachPill("rect", (rect, n) => {
      const mask = maskOf(n);
      if (!mask) {
        rect.setAttribute("fill", c.gateInactive);
        return;
      }
      const fill = fillOf(mask);
      rect.setAttribute("fill", fill);
      rect.setAttribute("data-gate-fill", fill);
      stamp(rect, mask, [n], "");
    });
    eachPill("text", (text, n) => {
      const mask = maskOf(n);
      // No halo to clear: _resetGatePills took the stroke, its width and
      // paint-order off every pill before any of this ran.
      if (!mask) {
        text.setAttribute("fill", c.gateTextInactive);
        return;
      }
      let ink = mask === 2 ? c.gateTextActive : readerInk;
      if (mask === 3) {
        ink = c.gateTextBoth;
        text.setAttribute("stroke", c.gateTextBothHalo);
        text.setAttribute("stroke-width", "2");
        text.setAttribute("paint-order", "stroke");
      }
      text.setAttribute("fill", ink);
      text.setAttribute("data-gate-fill", ink);
      stamp(text, mask, [n], "");
    });
  },

  /** A point ON a channel, at the JOIN between its two gates' legs and on the
   *  channel's centre line, in the group's OWN coordinate space, or null where it
   *  cannot be measured (integration channels, an unrendered chart, no
   *  getPointAtLength). Bounding-box and chord midpoints miss curved channels.
   *
   *  Generated channels carry their exact midpoint in SVG coordinates. The
   *  WeakMap and measurement below support older embedded SVGs only. Measuring
   *  forces layout (about 100 ms per channel), so answers are cached per SVG;
   *  a failure is never cached, and no answer is shared between SVGs. */
  _channelMidpoints: new WeakMap(),

  _channelMidpoint(svg, group, gateA, gateB) {
    if (!group) return null;
    const fixed = this._geometryMidpoint(group);
    if (fixed) return fixed;
    let memo = this._channelMidpoints.get(svg);
    if (!memo) {
      memo = new Map();
      this._channelMidpoints.set(svg, memo);
    }
    const key = gateA < gateB ? `${gateA}-${gateB}` : `${gateB}-${gateA}`;
    if (memo.has(key)) return memo.get(key);
    const point = this._measureChannelMidpoint(svg, group, gateA, gateB);
    if (point) memo.set(key, point);
    return point;
  },

  /** The measurement itself — see `_channelMidpoint`, which is what to call. */
  _measureChannelMidpoint(svg, group, gateA, gateB) {
    const legs = {};
    for (const gate of [gateA, gateB]) {
      const ribbons = [];
      for (const prefix of ["design", "personality"]) {
        const ribbon = this._walkRibbon(svg, svg.getElementById(`${prefix}-${gate}`), group);
        if (!ribbon) return null;
        ribbons.push(ribbon);
      }
      legs[gate] = ribbons;
    }
    // The cut across one leg, as the middle of its two outer corners.
    const cutMiddle = (gate, other) => {
      const corners = [];
      for (const i of [0, 1]) {
        const corner = this._cutCorner(legs[gate][i], legs[gate][1 - i], legs[other]);
        if (!corner) return null;
        corners.push(corner);
      }
      return { x: (corners[0].x + corners[1].x) / 2, y: (corners[0].y + corners[1].y) / 2 };
    };
    const a = cutMiddle(gateA, gateB);
    const b = cutMiddle(gateB, gateA);
    // Never a bare chord as a fallback: off the center line is the thing this
    // function exists to avoid, and the pill badge says the same thing honestly.
    if (!a || !b) return null;
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  },

  /** How finely a whole ribbon outline is walked to find roughly where it
   *  meets the next leg, close enough to aim the second, closer walk. */
  SKY_DOT_SAMPLES: 96,

  /** How far either side of that aim the second walk runs, and how many steps
   *  it takes. The cut across a channel is about 10 units long. */
  SKY_DOT_JOIN_SPAN: 12,
  SKY_DOT_JOIN_STEPS: 96,

  /** How much farther than the closest touch still counts as "on the cut", in
   *  user units: the one number that says where the cut stops. */
  SKY_DOT_JOIN_SLACK: 0.6,

  /** One ribbon prepared for measuring: its outline sampled in `group`'s space,
   *  or null (not a guess) when it cannot be walked. */
  _walkRibbon(svg, el, group) {
    if (!el || typeof el.getTotalLength !== "function" || typeof el.getPointAtLength !== "function") return null;
    const groupCTM = group.getScreenCTM();
    const elCTM = el.getScreenCTM();
    if (!groupCTM || !elCTM) return null;
    let total;
    try {
      total = el.getTotalLength();
    } catch {
      return null;
    }
    if (!total) return null;
    const ribbon = { svg, el, total, elCTM, inv: groupCTM.inverse(), outline: [] };
    ribbon.outline = this._ribbonPoints(ribbon, 0, total, this.SKY_DOT_SAMPLES);
    return ribbon.outline.length > 2 ? ribbon : null;
  },

  /** `steps` points along a ribbon's outline, from arc length `from` over `span`,
   *  in the channel group's space. The walk WRAPS: a path may start at a corner
   *  of the very cut being searched for. */
  _ribbonPoints(ribbon, from, span, steps) {
    const points = [];
    for (let i = 0; i < steps; i++) {
      let at = from + (span * i) / steps;
      at = ((at % ribbon.total) + ribbon.total) % ribbon.total;
      const walked = ribbon.el.getPointAtLength(at);
      const pt = ribbon.svg.createSVGPoint();
      pt.x = walked.x;
      pt.y = walked.y;
      const local = pt.matrixTransform(ribbon.elCTM).matrixTransform(ribbon.inv);
      points.push({ x: local.x, y: local.y, at });
    }
    return points;
  },

  /** Distance from a point to the nearest of several sampled CLOSED outlines. */
  _distanceToOutlines(point, ribbons) {
    let best = Infinity;
    for (const ribbon of ribbons) {
      const points = ribbon.outline;
      for (let i = 0; i < points.length; i++) {
        const d = this._distanceToSegment(point, points[i], points[(i + 1) % points.length]);
        if (d < best) best = d;
      }
    }
    return best;
  },

  /** Distance from `p` to the segment `a`–`b`. Measuring to the SEGMENTS and
   *  not the sampled points is what lets the coarse walk stay coarse. */
  _distanceToSegment(p, a, b) {
    const vx = b.x - a.x;
    const vy = b.y - a.y;
    const wx = p.x - a.x;
    const wy = p.y - a.y;
    const len = vx * vx + vy * vy;
    let t = len ? (wx * vx + wy * vy) / len : 0;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(wx - t * vx, wy - t * vy);
  },

  /** Where `ribbon`'s cut against `otherLeg` meets the channel's outer edge. */
  _cutCorner(ribbon, mate, otherLeg) {
    let aim = 0;
    let closest = Infinity;
    for (const point of ribbon.outline) {
      const gap = this._distanceToOutlines(point, otherLeg);
      if (gap < closest) {
        closest = gap;
        aim = point.at;
      }
    }
    const span = this.SKY_DOT_JOIN_SPAN;
    const fine = this._ribbonPoints(ribbon, aim - span, span * 2, this.SKY_DOT_JOIN_STEPS);
    let touching = Infinity;
    for (const point of fine) {
      point.gap = this._distanceToOutlines(point, otherLeg);
      if (point.gap < touching) touching = point.gap;
    }
    const onCut = fine.filter((point) => point.gap <= touching + this.SKY_DOT_JOIN_SLACK);
    let widest = -1;
    for (const point of onCut) {
      point.across = this._distanceToOutlines(point, [mate]);
      if (point.across > widest) widest = point.across;
    }
    // A twentieth of a unit: wide enough that a sampled corner counts as "as
    // far out as it gets", narrow enough that the inner half of a cut does not.
    const FLAT = 0.05;
    let corner = null;
    for (const point of onCut) {
      if (point.across < widest - FLAT) continue;
      if (!corner || point.gap < corner.gap) corner = point;
    }
    return corner;
  },

  /** Highlight one sky channel by its `data-sky-ch` key ("18-58", either order).
   *  Returns the elements to focus; empty when the key names nothing, so a
   *  stale row selects nothing rather than everything. */
  highlightSkyChannel(container, channelKey) {
    const svg = container && container.querySelector ? container.querySelector("svg") : null;
    if (!svg || !channelKey) return [];
    const [a, b] = String(channelKey).split("-");
    const hit = (key) => Array.from(svg.querySelectorAll(`[data-sky-ch="${key}"]`));
    return hit(`${a}-${b}`).concat(hit(`${b}-${a}`));
  },

  _colorChannel(svg, ch, c, patId = "both-stripe", mark = null) {
    const fillFor = (consciousness) =>
      consciousness === "both"
        ? `url(#${patId})`
        : consciousness === "unconscious"
          ? c.channelDesign
          : c.channelPersonality;
    this._paintChannel(svg, ch, fillFor(ch.gate_a_consciousness), fillFor(ch.gate_b_consciousness), mark);
  },

  /** The colour a channel wears in the circuit view: its family's, keyed on the
   *  engine's `circuit_group` word; an unknown group gets the Personality ink. */
  circuitFill(group, c) {
    switch (String(group || "").toLowerCase()) {
      case "integration":
        return c.circuitIntegration;
      case "individual":
        return c.circuitIndividual;
      case "collective":
        return c.circuitCollective;
      case "tribal":
        return c.circuitTribal;
      default:
        return c.channelPersonality;
    }
  },

  /** Fill one defined channel with an arbitrary fill per half. */
  _paintChannel(svg, ch, fillA, fillB, mark = null) {
    fillA = this._channelFill(svg, ch.gate_a, fillA);
    fillB = this._channelFill(svg, ch.gate_b, fillB);
    // ── Try pixel-perfect border path fill (same fill both halves) ──
    if (fillA === fillB) {
      const borderPath = this._findBorderPath(svg, ch.gate_a, ch.gate_b);
      if (borderPath) {
        borderPath.setAttribute("fill", fillA);
        // Match stroke to fill for clean anti-aliased edges
        borderPath.setAttribute(
          "stroke",
          borderPath.getAttribute("data-channel-shape") || fillA.startsWith("url(") ? "none" : fillA,
        );
        if (mark) mark(borderPath, [ch.gate_a, ch.gate_b]);
        return;
      }
    }

    // ── Fallback: group fill or individual strip paths ──
    const groupId = `${ch.gate_a}-${ch.gate_b}`;
    const groupIdAlt = `${ch.gate_b}-${ch.gate_a}`;
    const group = svg.getElementById(groupId) || svg.getElementById(groupIdAlt);

    if (group && fillA === fillB) {
      group.setAttribute("fill", fillA);
      if (!fillA.startsWith("url(")) {
        group.querySelectorAll("path, rect").forEach((p) => {
          p.setAttribute("stroke", fillA);
          p.setAttribute("stroke-width", "0.5");
        });
      }
      if (mark) mark(group, [ch.gate_a, ch.gate_b]);
      return;
    }

    this._fillGatePaths(svg, ch.gate_a, fillA, mark ? (el) => mark(el, [ch.gate_a]) : null);
    this._fillGatePaths(svg, ch.gate_b, fillB, mark ? (el) => mark(el, [ch.gate_b]) : null);
  },

  /** Fill a single gate's design/personality paths with the given fill. */
  _fillGatePaths(svg, gate, fill, mark = null) {
    fill = this._channelFill(svg, gate, fill);
    for (const el of this._gatePaths(svg, gate)) {
      el.setAttribute("fill", fill);
      if (el.id === `gate-${gate}`) {
        el.setAttribute("stroke", "none");
      } else if (!fill.startsWith("url(")) {
        el.setAttribute("stroke", fill);
        el.setAttribute("stroke-width", "0.5");
      }
      el.style.display = "";
      if (mark) mark(el);
    }
  },

  /**
   * Compute the center of a gate's paths in the channel group's local coordinate space.
   */
  _gateCenterInGroup(svg, gate, group) {
    let sx = 0,
      sy = 0,
      n = 0;
    // getScreenCTM() is null for an element that is not rendered — a chart on a
    // hidden tab. Callers treat null as "fill the gate's own leg".
    const groupCTM = group.getScreenCTM();
    if (!groupCTM) return null;
    const groupCTMInv = groupCTM.inverse();
    for (const el of this._gatePaths(svg, gate)) {
      const bb = el.getBBox();
      const pt = svg.createSVGPoint();
      pt.x = bb.x + bb.width / 2;
      pt.y = bb.y + bb.height / 2;
      const screen = pt.matrixTransform(el.getScreenCTM());
      const local = screen.matrixTransform(groupCTMInv);
      sx += local.x;
      sy += local.y;
      n++;
    }
    return n ? { x: sx / n, y: sy / n } : null;
  },

  /** Fill a hanging gate's half of a channel. */
  _colorHangingGate(svg, hg, c, patId = "both-stripe") {
    const fill =
      hg.consciousness === "both"
        ? `url(#${patId})`
        : hg.consciousness === "unconscious"
          ? c.channelDesign
          : c.channelPersonality;
    this._paintHangingGate(svg, hg.gate, hg.partner_gate, fill);
  },

  /** Paint one hanging gate's half-channel with an arbitrary fill. */
  _paintHangingGate(svg, gate, partnerGate, fill, mark = null) {
    // The generated silhouette already ends at the channel's exact midpoint.
    // It needs neither a bounding-box measurement nor a runtime clipping mask.
    if (svg.getElementById(`gate-${gate}`)) {
      this._fillGatePaths(svg, gate, fill, mark);
      return;
    }
    const group = svg.getElementById(`${gate}-${partnerGate}`) || svg.getElementById(`${partnerGate}-${gate}`);
    // Integration gates (10/20/34/57) share one combined SVG group with no
    // per-channel subgroup, so the clip can't be computed: fill the gate's leg.
    if (!group) {
      this._fillGatePaths(svg, gate, fill, mark);
      return;
    }

    const gc = this._gateCenterInGroup(svg, gate, group);
    const pc = this._gateCenterInGroup(svg, partnerGate, group);
    if (!gc || !pc) {
      this._fillGatePaths(svg, gate, fill, mark);
      return;
    }

    // Fill ALL paths in the channel group (both gates' geometry)
    group.querySelectorAll("path, rect").forEach((p) => {
      p.setAttribute("fill", fill);
      if (!fill.startsWith("url(")) {
        p.setAttribute("stroke", fill);
        p.setAttribute("stroke-width", "0.5");
      }
      p.style.display = "";
      if (mark) mark(p);
    });

    // Clip the group at the perpendicular bisector, showing only the active gate's half
    const mx = (gc.x + pc.x) / 2,
      my = (gc.y + pc.y) / 2;
    const dx = pc.x - gc.x,
      dy = pc.y - gc.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / len,
      py = dx / len; // perpendicular
    const gx = -dx / len,
      gy = -dy / len; // toward active gate
    const D = 500;
    const pts = [
      [mx + D * px, my + D * py],
      [mx + D * px + D * gx, my + D * py + D * gy],
      [mx - D * px + D * gx, my - D * py + D * gy],
      [mx - D * px, my - D * py],
    ];

    let defs = svg.querySelector("defs");
    if (!defs) {
      defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      svg.insertBefore(defs, svg.firstChild);
    }
    const clipId = `hg-clip-${gate}-${partnerGate}`;
    const existing = defs.querySelector(`#${CSS.escape(clipId)}`);
    if (existing) existing.remove();

    const clipEl = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
    clipEl.id = clipId;
    const polygon = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
    polygon.setAttribute("points", pts.map((p) => p.join(",")).join(" "));
    clipEl.appendChild(polygon);
    defs.appendChild(clipEl);
    group.setAttribute("clip-path", `url(#${clipId})`);
  },

  /**
   * Highlight gate pills in the SVG.
   *
   * A tiered drawing (paintTiers) stamps every pill with the fill and stroke it
   * was painted with, and a pill that carries those goes back to them when the
   * hover leaves. Re-deriving the resting look from `activeGates` would work
   * for an ordinary chart and quietly flatten a tiered one to two states on the
   * first hover — the drawing's whole point, gone, with nothing to see it in.
   *
   * @param {HTMLElement} container
   * @param {Set<number>} gateNums — gates to highlight (empty = clear all)
   * @param {Set<number>} activeGates — active gate numbers for base colours
   */
  highlightGates(container, gateNums, activeGates) {
    const centersGroup = container.querySelector("#Centers");
    if (!centersGroup) return;
    const c = this.colors();
    centersGroup.querySelectorAll("rect[id]").forEach((rect) => {
      const n = parseInt(rect.id, 10);
      if (isNaN(n) || rect.id.includes("-")) return;
      const tierFill = rect.getAttribute("data-tier-fill");
      const ring = rect.getAttribute("data-gate-ring");
      const dash = rect.getAttribute("data-gate-dash");
      if (gateNums.has(n)) {
        rect.setAttribute("fill", c.gateHighlight);
        // The gold is the whole point of a hover; a ring on top of it only
        // muddies it, and the memo puts it back on the way out.
        if (ring !== null) this._ringPill(rect, null);
        if (tierFill !== null || dash !== null) rect.removeAttribute("stroke-dasharray");
        return;
      }
      if (tierFill !== null) {
        this._applyTierStyle(rect, {
          fill: tierFill,
          stroke: rect.getAttribute("data-tier-stroke") || "",
          width: 1,
          dash: rect.getAttribute("data-tier-dash") || "",
        });
        return;
      }
      const resting = rect.getAttribute("data-gate-fill");
      rect.setAttribute("fill", resting !== null ? resting : activeGates.has(n) ? c.gateActive : c.gateInactive);
      // A bridge's ring is dashed; the memo says so, the way it says the colour.
      if (ring !== null) this._ringPill(rect, ring, dash);
    });
    centersGroup.querySelectorAll("text[id]").forEach((text) => {
      const n = parseInt(text.id, 10);
      if (isNaN(n) || text.id.includes("-")) return;
      const tierFill = text.getAttribute("data-tier-fill");
      const memo = text.getAttribute("data-gate-fill");
      const resting =
        tierFill !== null
          ? tierFill
          : memo !== null
            ? memo
            : activeGates.has(n)
              ? c.gateTextActive
              : c.gateTextInactive;
      text.setAttribute("fill", gateNums.has(n) ? c.gateHighlightText : resting);
    });
  },
};

// One reverse lookup from one table, so the two cannot disagree with each
// other. A plain for-in, not Object.entries, on purpose: this runs at
// evaluation, and test_frontend_route_chunks.py flags any global it reads.
for (const center in Bodygraph.CENTER_GATES) {
  const gates = Bodygraph.CENTER_GATES[center];
  for (let i = 0; i < gates.length; i += 1) Bodygraph.GATE_TO_CENTER[gates[i]] = center;
}

// And the other reverse lookup, from the other table, for the same reason and
// with the same care — plain index loops, nothing reached for.
for (let i = 0; i < Bodygraph.CHANNELS.length; i += 1) {
  const pair = Bodygraph.CHANNELS[i];
  for (let end = 0; end < 2; end += 1) {
    const gate = pair[end];
    const partner = pair[1 - end];
    if (!Bodygraph.GATE_PARTNERS[gate]) Bodygraph.GATE_PARTNERS[gate] = [];
    if (Bodygraph.GATE_PARTNERS[gate].indexOf(partner) === -1) Bodygraph.GATE_PARTNERS[gate].push(partner);
  }
}

// The export convention — see the boot list in js/app.js. Read by name on the
// pages that draw a bodygraph and probed for on the ones that may not load it.
window.Bodygraph = Bodygraph;
